import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../services/supabase';
import { useAuth } from '../../context/AuthContext';
import {
  useChurchPlanner,
  usePlannerRoles,
  type PlannerAssignment,
  type PlannerRole,
  type PlannerSubsection,
  type PlannerSubsectionAssignment,
} from '../../hooks/useChurchPlanner';
import {
  PlannerCard,
  PlannerError,
  PlannerHeading,
  PlannerKicker,
  PlannerPage,
  PlannerSubtle,
  plannerColors,
} from './plannerTheme';
import { formatPlannerStatusLabel, normalizePlannerStatus } from './plannerStatus';
import { formatSongTemplateLengthDisplay, parseSongTemplateDurationInput } from './songDuration';
import { buildTimeslotDragDirections, reorderDraggedTimeslots } from './timeslotDrag';
import { PLANNER_TEAM_DEFINITIONS, getPlannerRoleTeamGroup, resolveTeamIdFromRole } from '../../hooks/usePlannerInsights';
import { hasAnyPermission, Permission } from '../../utils/roles';

type AssignableMember = {
  id: string;
  full_name?: string | null;
  email?: string | null;
  title?: string | null;
  avatar?: string | null;
  roles?: string[] | null;
};

type SongTemplate = {
  id: string;
  title: string;
  artist?: string | null;
  key_signature?: string | null;
  length_minutes: number;
  length_seconds?: number | null;
  bpm?: number | null;
  last_scheduled_date?: string | null;
  sequence: number;
  song_sequence?: string | null;
  video_url?: string | null;
  notes?: string | null;
  lyrics_url?: string | null;
  chord_chart_url?: string | null;
  is_active: boolean;
};

type TimelineSubsection = {
  subsection: PlannerSubsection;
  durationMinutes: number;
};

const DEFAULT_ORDER_SECTIONS = [
  'Pre-Service',
  'Worship',
  'Welcome/Annoucements',
  'Voice of Faith Choir',
  'Baby Dedication',
  'Offering',
  'Song of Preperation',
  'Sermon',
];

const compareSongTemplatesByTitle = (a: SongTemplate, b: SongTemplate): number => {
  const titleCompare = (a.title || '').trim().localeCompare((b.title || '').trim(), undefined, {
    sensitivity: 'base',
  });
  if (titleCompare !== 0) return titleCompare;
  return (a.id || '').localeCompare(b.id || '');
};

const findAutoMatchedSongTemplate = (songs: SongTemplate[], rawSearchTerm: string): SongTemplate | null => {
  const term = rawSearchTerm.trim().toLowerCase();
  if (!term) return null;

  const exactMatch = songs.find((song) => {
    const title = song.title?.trim().toLowerCase() || '';
    const artist = song.artist?.trim().toLowerCase() || '';
    return title === term || artist === term;
  });
  if (exactMatch) return exactMatch;

  const partialMatches = songs.filter((song) => {
    const title = song.title?.toLowerCase() || '';
    const artist = song.artist?.toLowerCase() || '';
    return title.includes(term) || artist.includes(term);
  });

  return partialMatches.length === 1 ? partialMatches[0] : null;
};

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.8rem;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const HeroCard = styled(PlannerCard)`
  position: relative;
  overflow: visible;
  background:
    radial-gradient(circle at 12% 18%, rgba(229, 211, 179, 0.22), transparent 46%),
    radial-gradient(circle at 88% 90%, rgba(255, 255, 255, 0.05), transparent 42%),
    ${plannerColors.surface};
`;

const HeroMetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const HeroMetaPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  border: 1px solid ${plannerColors.border};
  background: rgba(0, 0, 0, 0.18);
  border-radius: 999px;
  padding: 0.2rem 0.55rem;
  font-size: 0.72rem;
  color: ${plannerColors.text};
`;

const BackButton = styled.button`
  border: 1px solid ${plannerColors.border};
  background: ${plannerColors.surfaceHover};
  color: ${plannerColors.text};
  border-radius: 10px;
  padding: 0.45rem 0.7rem;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  cursor: pointer;
`;

const TabRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const TabStrip = styled.div`
  margin-top: 1rem;
  padding-top: 0.85rem;
  border-top: 1px solid rgba(229, 211, 179, 0.2);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.02), transparent 70%);
`;

const TabButton = styled.button<{ $active: boolean }>`
  position: relative;
  border: 1px solid
    ${({ $active }) => ($active ? 'rgba(229, 211, 179, 0.7)' : 'rgba(229, 211, 179, 0.26)')};
  background: ${({ $active }) =>
    $active
      ? 'linear-gradient(180deg, rgba(229, 211, 179, 0.24), rgba(229, 211, 179, 0.08))'
      : 'linear-gradient(180deg, rgba(255, 255, 255, 0.06), rgba(0, 0, 0, 0.08))'};
  color: ${({ $active }) => ($active ? plannerColors.text : plannerColors.muted)};
  border-radius: 12px 12px 9px 9px;
  padding: 0.52rem 0.85rem;
  font-size: 0.74rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  cursor: pointer;
  transition: border-color 140ms ease, color 140ms ease, transform 140ms ease, box-shadow 140ms ease;
  box-shadow: ${({ $active }) => ($active ? '0 10px 18px rgba(0, 0, 0, 0.28)' : 'none')};

  &:hover {
    color: ${plannerColors.text};
    border-color: rgba(229, 211, 179, 0.5);
    transform: translateY(-1px);
  }

  &::after {
    content: '';
    position: absolute;
    left: 10px;
    right: 10px;
    bottom: -7px;
    height: 3px;
    border-radius: 999px;
    background: ${plannerColors.accent};
    opacity: ${({ $active }) => ($active ? 1 : 0)};
    transform: ${({ $active }) => ($active ? 'scaleX(1)' : 'scaleX(0.4)')};
    transition: opacity 140ms ease, transform 140ms ease;
  }
`;

const SectionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.95rem;
`;

const staggerIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const StaggerItem = styled.div<{ $delayMs: number }>`
  opacity: 0;
  transform: translateY(8px);
  animation: ${staggerIn} 380ms cubic-bezier(0.22, 0.61, 0.36, 1) forwards;
  animation-delay: ${({ $delayMs }) => `${$delayMs}ms`};
  will-change: opacity, transform;
`;

const TimelineSectionRow = styled.div`
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr);
  gap: 0.65rem;
  align-items: stretch;
`;

const TimelineRail = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const TimelineNode = styled.div<{ $state: 'complete' | 'active' | 'upcoming' }>`
  width: 14px;
  height: 14px;
  border-radius: 999px;
  border: 2px solid
    ${({ $state }) =>
      $state === 'complete' ? plannerColors.accent : $state === 'active' ? plannerColors.accent : plannerColors.border};
  background: ${({ $state }) => ($state === 'upcoming' ? plannerColors.surface : plannerColors.accentSoft)};
  box-shadow: ${({ $state }) => ($state === 'active' ? '0 0 0 4px rgba(229, 211, 179, 0.12)' : 'none')};
`;

const TimelineConnector = styled.div`
  position: relative;
  width: 2px;
  flex: 1;
  min-height: 56px;
  margin-top: 6px;
  border-radius: 999px;
  background: ${plannerColors.border};
  overflow: hidden;
`;

const TimelineConnectorFill = styled.div<{ $heightPct: number }>`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: ${({ $heightPct }) => `${Math.max(0, Math.min(100, $heightPct))}%`};
  background: ${plannerColors.accent};
  border-radius: 999px;
  transition: height 260ms ease;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.55rem;
`;

const SectionHeaderMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.55rem;
`;

const SectionBadge = styled.span`
  border: 1px solid ${plannerColors.border};
  border-radius: 999px;
  padding: 0.2rem 0.52rem;
  color: ${plannerColors.muted};
  font-size: 0.66rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const SectionCard = styled.div`
  border: 1px solid ${plannerColors.border};
  border-radius: 12px;
  background: ${plannerColors.surfaceHover};
  padding: 0.72rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const SectionDurationFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  border-top: 1px solid rgba(229, 211, 179, 0.18);
  padding-top: 0.55rem;
`;

const SectionDurationLabel = styled.span`
  color: ${plannerColors.muted};
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const SectionDurationValue = styled.span`
  color: ${plannerColors.text};
  font-size: 0.8rem;
  font-weight: 800;
`;

const ServiceDurationSummary = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1rem;
  border: 1px solid rgba(229, 211, 179, 0.26);
  border-radius: 14px;
  background: linear-gradient(180deg, rgba(229, 211, 179, 0.12), rgba(0, 0, 0, 0.08));
  padding: 0.8rem 0.95rem;
`;

const ServiceDurationSummaryLabel = styled.span`
  color: ${plannerColors.muted};
  font-size: 0.74rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const ServiceDurationSummaryValue = styled.span`
  color: ${plannerColors.text};
  font-size: 0.95rem;
  font-weight: 800;
`;

const SubsectionRow = styled.div<{ $canDrag?: boolean; $isDragging?: boolean; $isDropTarget?: boolean }>`
  border: 1px solid ${plannerColors.border};
  border-radius: 12px;
  background: ${({ $isDropTarget }) => ($isDropTarget ? plannerColors.surfaceHover : plannerColors.surface)};
  padding: 0.7rem;
  display: grid;
  grid-template-columns: 66px minmax(0, 1fr);
  gap: 0.6rem;
  transition: border-color 180ms ease, box-shadow 180ms ease, transform 180ms ease;
  opacity: ${({ $isDragging }) => ($isDragging ? 0.56 : 1)};
  cursor: ${({ $canDrag }) => ($canDrag ? 'default' : 'default')};
  border-color: ${({ $isDropTarget }) => ($isDropTarget ? plannerColors.accent : plannerColors.border)};
  box-shadow: ${({ $isDropTarget }) => ($isDropTarget ? '0 0 0 3px rgba(229, 211, 179, 0.12)' : 'none')};
`;

const ActiveSubsectionRow = styled(SubsectionRow)`
  border-color: ${plannerColors.accent};
  box-shadow: 0 0 0 3px rgba(229, 211, 179, 0.12);
  transform: translateY(-1px);
`;

const SubsectionDurationColumn = styled.div`
  border: 1px solid ${plannerColors.border};
  border-radius: 12px;
  background: ${plannerColors.surfaceHover};
  padding: 0.45rem 0.35rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 0.2rem;
  min-height: 66px;
`;

const SubsectionDurationLabel = styled.span`
  color: ${plannerColors.muted};
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const SubsectionContent = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const SubsectionBodyRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
`;

const SubsectionHeadingGroup = styled.div`
  min-width: 0;
  display: flex;
  align-items: flex-start;
  gap: 0.45rem;
`;

const TimeslotDragHandle = styled.span<{ $disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.9rem;
  min-width: 1.9rem;
  height: 1.9rem;
  border-radius: 10px;
  border: 1px dashed ${plannerColors.border};
  background: ${plannerColors.surfaceHover};
  color: ${plannerColors.muted};
  font-size: 1rem;
  line-height: 1;
  user-select: none;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'grab')};
  opacity: ${({ $disabled }) => ($disabled ? 0.45 : 1)};

  &:active {
    cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'grabbing')};
  }
`;

const SubsectionTitle = styled.h4`
  margin: 0;
  font-size: 0.92rem;
  color: ${plannerColors.text};
`;

const Duration = styled.span`
  color: ${plannerColors.muted};
  font-size: 0.76rem;
`;

const SubsectionDurationPill = styled(Duration)`
  display: inline-flex;
  align-items: center;
  border: 1px solid ${plannerColors.border};
  border-radius: 999px;
  padding: 0.18rem 0.48rem;
  background: ${plannerColors.surfaceHover};
  white-space: nowrap;
`;

const TimeslotDescription = styled.p`
  margin: 0;
  color: ${plannerColors.muted};
  font-size: 0.8rem;
  line-height: 1.45;
  white-space: pre-wrap;
`;

const TimeslotSongLink = styled.button`
  border: 1px solid ${plannerColors.border};
  border-radius: 10px;
  background: ${plannerColors.surfaceHover};
  color: ${plannerColors.text};
  padding: 0.5rem 0.6rem;
  display: grid;
  gap: 0.42rem;
  text-align: left;
  cursor: pointer;

  &:hover {
    border-color: ${plannerColors.accent};
  }
`;

const TimeslotSongTitle = styled.span`
  color: ${plannerColors.text};
  font-size: 0.84rem;
  font-weight: 800;
  letter-spacing: 0.01em;
`;

const TimeslotSongMetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.38rem;
`;

const TimeslotSongMetaChip = styled.span`
  border: 1px solid ${plannerColors.border};
  border-radius: 999px;
  background: ${plannerColors.surface};
  color: ${plannerColors.muted};
  padding: 0.16rem 0.44rem;
  font-size: 0.68rem;
  font-weight: 700;
`;

const SectionActionsFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  padding-top: 0.15rem;
`;

const TimelineAddSectionRow = styled.div`
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr);
  gap: 0.65rem;
  align-items: stretch;
`;

const TimelineAddSectionRail = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const TimelineBottomConnector = styled.div`
  width: 2px;
  min-height: 14px;
  border-radius: 999px;
  background: ${plannerColors.border};
`;

const AddSectionRailButton = styled.button`
  border: none;
  background: transparent;
  color: ${plannerColors.accent};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 0.2rem;
  padding: 0;
  cursor: pointer;
`;

const AddSectionRailText = styled.span`
  margin-top: 0.2rem;
  color: ${plannerColors.muted};
  font-size: 0.66rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  text-align: center;
`;

const TimelineAddSectionSpacer = styled.div`
  min-height: 1px;
`;

const FooterActionRow = styled.button`
  width: 100%;
  border: 1px dashed ${plannerColors.border};
  border-radius: 12px;
  background: ${plannerColors.surface};
  color: ${plannerColors.text};
  padding: 0.65rem 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-align: left;
  cursor: pointer;
`;

const FooterActionIcon = styled.span`
  width: 26px;
  height: 26px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${plannerColors.accentSoft};
  color: ${plannerColors.accent};
  flex: 0 0 auto;
`;

const FooterActionIconMark = styled.span`
  font-size: 1rem;
  line-height: 1;
  font-weight: 900;
`;

const FooterActionText = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 0.1rem;
`;

const FooterActionTitle = styled.span`
  color: ${plannerColors.text};
  font-size: 0.8rem;
  font-weight: 800;
`;

const FooterActionMeta = styled.span`
  color: ${plannerColors.muted};
  font-size: 0.72rem;
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
`;

const PersonChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  border: 1px solid ${plannerColors.border};
  background: ${plannerColors.surfaceHover};
  border-radius: 999px;
  padding: 0.2rem 0.42rem;
  max-width: 100%;
  font-size: 0.72rem;
`;

const PersonAvatar = styled.img`
  width: 20px;
  height: 20px;
  border-radius: 999px;
  object-fit: cover;
  flex: 0 0 auto;
`;

const PersonAvatarFallback = styled.span`
  width: 20px;
  height: 20px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${plannerColors.accentSoft};
  color: ${plannerColors.accent};
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.03em;
  flex: 0 0 auto;
`;

const PersonChipName = styled.span`
  color: ${plannerColors.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 220px;
`;

const RemoveChipButton = styled.button`
  border: none;
  background: transparent;
  color: ${plannerColors.muted};
  font-size: 0.72rem;
  cursor: pointer;
`;

const ActionButton = styled.button`
  border: 1px solid ${plannerColors.border};
  border-radius: 8px;
  background: ${plannerColors.surfaceHover};
  color: ${plannerColors.text};
  padding: 0.35rem 0.55rem;
  font-size: 0.66rem;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  cursor: pointer;
`;

const MenuWrap = styled.div`
  position: relative;
`;

const MenuButton = styled.button`
  border: 1px solid ${plannerColors.border};
  border-radius: 8px;
  background: ${plannerColors.surfaceHover};
  color: ${plannerColors.text};
  padding: 0.3rem 0.55rem;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  cursor: pointer;
`;

const MenuPanel = styled.div`
  position: absolute;
  right: 0;
  top: calc(100% + 0.35rem);
  min-width: 180px;
  border: 1px solid ${plannerColors.border};
  background: ${plannerColors.surface};
  border-radius: 10px;
  padding: 0.25rem;
  z-index: 20;
`;

const MenuItem = styled.button<{ $danger?: boolean }>`
  width: 100%;
  border: none;
  background: transparent;
  color: ${({ $danger }) => ($danger ? plannerColors.danger : plannerColors.text)};
  border-radius: 8px;
  padding: 0.45rem 0.55rem;
  text-align: left;
  font-size: 0.78rem;
  cursor: pointer;

  &:hover {
    background: ${({ $danger }) => ($danger ? 'rgba(255, 69, 58, 0.14)' : plannerColors.surfaceHover)};
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 0.65rem;
`;

const TeamSectionBlock = styled.section`
  border: 1px solid ${plannerColors.border};
  border-radius: 12px;
  background: ${plannerColors.surfaceHover};
  padding: 0.8rem 0.9rem;
  margin-top: 0.9rem;
`;

const TeamSectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.7rem;
`;

const TeamSectionTitle = styled.h4`
  margin: 0;
  color: ${plannerColors.text};
  font-size: 0.86rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const CountBadge = styled.span`
  border: 1px solid ${plannerColors.border};
  border-radius: 999px;
  padding: 0.2rem 0.5rem;
  color: ${plannerColors.muted};
  font-size: 0.66rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

const TeamCard = styled.div`
  border: 1px solid ${plannerColors.border};
  border-radius: 10px;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.02), transparent 70%),
    ${plannerColors.surface};
  padding: 0.7rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const TeamRoleTitle = styled.strong`
  color: ${plannerColors.text};
  font-size: 0.88rem;
`;

const TeamPersonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
`;

const TeamPersonAvatar = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 999px;
  object-fit: cover;
  flex: 0 0 auto;
`;

const TeamPersonAvatarFallback = styled.span`
  width: 24px;
  height: 24px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${plannerColors.accentSoft};
  color: ${plannerColors.accent};
  font-size: 0.66rem;
  font-weight: 800;
  letter-spacing: 0.02em;
  flex: 0 0 auto;
`;

const TeamPersonName = styled.span<{ $unassigned?: boolean }>`
  color: ${({ $unassigned }) => ($unassigned ? '#ff7b72' : plannerColors.text)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TeamMeta = styled.div`
  color: ${plannerColors.muted};
  font-size: 0.78rem;
`;

const TeamStatusPill = styled.span<{ $tone: 'ok' | 'warn' | 'bad' }>`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  border-radius: 999px;
  padding: 0.16rem 0.5rem;
  font-size: 0.66rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  border: 1px solid
    ${({ $tone }) =>
      $tone === 'ok'
        ? 'rgba(52, 199, 89, 0.4)'
        : $tone === 'bad'
          ? 'rgba(255, 69, 58, 0.45)'
          : 'rgba(255, 204, 0, 0.45)'};
  background:
    ${({ $tone }) =>
      $tone === 'ok'
        ? 'rgba(52, 199, 89, 0.16)'
        : $tone === 'bad'
          ? 'rgba(255, 69, 58, 0.14)'
          : 'rgba(255, 204, 0, 0.15)'};
  color:
    ${({ $tone }) =>
      $tone === 'ok' ? plannerColors.success : $tone === 'bad' ? plannerColors.danger : plannerColors.warning};
`;

const TeamActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  margin-top: 0.25rem;
`;

const TeamActionButton = styled(ActionButton)`
  flex: 1 1 auto;
`;

const FileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  margin-top: 0.8rem;
`;

const FileCategoryBlock = styled.section`
  border: 1px solid ${plannerColors.border};
  border-radius: 12px;
  background: ${plannerColors.surfaceHover};
  padding: 0.8rem 0.9rem;
`;

const FileCategoryHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.7rem;
  margin-bottom: 0.7rem;
`;

const FileCategoryTitle = styled.h4`
  margin: 0;
  color: ${plannerColors.text};
  font-size: 0.82rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const FileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 0.6rem;
`;

const FileRow = styled.a`
  border: 1px solid ${plannerColors.border};
  border-radius: 10px;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.02), transparent 70%),
    ${plannerColors.surface};
  padding: 0.65rem 0.75rem;
  display: grid;
  gap: 0.35rem;
  color: ${plannerColors.text};
  text-decoration: none;
`;

const FileTitle = styled.span`
  color: ${plannerColors.text};
  font-size: 0.85rem;
  font-weight: 700;
`;

const FileMetaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`;

const FileMetaPill = styled.span`
  border: 1px solid ${plannerColors.border};
  border-radius: 999px;
  padding: 0.12rem 0.45rem;
  color: ${plannerColors.muted};
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: radial-gradient(circle at 20% 10%, rgba(255, 255, 255, 0.08), rgba(0, 0, 0, 0.72));
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1.2rem;
`;

const PickerCard = styled.div`
  width: min(620px, 100%);
  max-height: 88vh;
  overflow-y: auto;
  border: 1px solid rgba(229, 211, 179, 0.22);
  border-radius: 18px;
  background:
    radial-gradient(circle at top right, rgba(229, 211, 179, 0.12), transparent 42%),
    ${plannerColors.surface};
  box-shadow:
    0 18px 44px rgba(0, 0, 0, 0.45),
    inset 0 1px 0 rgba(255, 255, 255, 0.04);
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(229, 211, 179, 0.28);
    border-radius: 999px;
  }
`;

const PickerCardTall = styled(PickerCard)`
  overflow: hidden;
`;

const ModalBody = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding-right: 0.1rem;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(229, 211, 179, 0.26);
    border-radius: 999px;
  }
`;

const ModalFooter = styled.div`
  margin-top: 0.2rem;
  padding-top: 0.7rem;
  border-top: 1px solid rgba(229, 211, 179, 0.2);
  background: linear-gradient(180deg, rgba(0, 0, 0, 0), rgba(9, 9, 11, 0.55));
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
`;

const SearchInput = styled.input`
  border: 1px solid ${plannerColors.border};
  border-radius: 12px;
  background: ${plannerColors.surfaceHover};
  color: ${plannerColors.text};
  padding: 0.62rem 0.72rem;
  transition: border-color 140ms ease, box-shadow 140ms ease, background-color 140ms ease;

  &::placeholder {
    color: ${plannerColors.muted};
  }

  &:focus {
    outline: none;
    border-color: rgba(229, 211, 179, 0.5);
    box-shadow: 0 0 0 3px rgba(229, 211, 179, 0.14);
  }
`;

const PickerList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const PickerItem = styled.button`
  border: 1px solid ${plannerColors.border};
  border-radius: 12px;
  background: ${plannerColors.surfaceHover};
  color: ${plannerColors.text};
  text-align: left;
  padding: 0.62rem 0.7rem;
  display: grid;
  gap: 0.22rem;
  cursor: pointer;
  transition: border-color 140ms ease, transform 140ms ease, background-color 140ms ease;

  &:hover {
    border-color: rgba(229, 211, 179, 0.45);
    background: rgba(255, 255, 255, 0.06);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    transform: none;
  }
`;

const ModalTitle = styled.h3`
  margin: 0;
  color: ${plannerColors.text};
  font-size: 1.05rem;
  font-weight: 800;
  letter-spacing: 0.01em;
`;

const ModalTitleInline = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const FormLabel = styled.label`
  color: ${plannerColors.muted};
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const FormInput = styled.input`
  border: 1px solid ${plannerColors.border};
  border-radius: 12px;
  background: ${plannerColors.surfaceHover};
  color: ${plannerColors.text};
  padding: 0.62rem 0.72rem;
  transition: border-color 140ms ease, box-shadow 140ms ease, background-color 140ms ease;

  &::placeholder {
    color: ${plannerColors.muted};
  }

  &:focus {
    outline: none;
    border-color: rgba(229, 211, 179, 0.5);
    box-shadow: 0 0 0 3px rgba(229, 211, 179, 0.14);
  }
`;

const FormTextarea = styled.textarea`
  border: 1px solid ${plannerColors.border};
  border-radius: 12px;
  background: ${plannerColors.surfaceHover};
  color: ${plannerColors.text};
  padding: 0.62rem 0.72rem;
  min-height: 98px;
  resize: vertical;
  transition: border-color 140ms ease, box-shadow 140ms ease, background-color 140ms ease;

  &:focus {
    outline: none;
    border-color: rgba(229, 211, 179, 0.5);
    box-shadow: 0 0 0 3px rgba(229, 211, 179, 0.14);
  }
`;

const ToggleRow = styled.label`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.8rem;
  border: 1px solid ${plannerColors.border};
  border-radius: 12px;
  background: ${plannerColors.surfaceHover};
  padding: 0.75rem 0.85rem;
`;

const ToggleText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.18rem;
`;

const ToggleTitle = styled.span`
  color: ${plannerColors.text};
  font-size: 0.88rem;
  font-weight: 700;
`;

const ToggleHelp = styled.span`
  color: ${plannerColors.muted};
  font-size: 0.78rem;
  line-height: 1.4;
`;

const ToggleCheckbox = styled.input`
  width: 18px;
  height: 18px;
  margin-top: 0.1rem;
  accent-color: ${plannerColors.accent};
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
`;

const EmptyHint = styled.p`
  margin: 0;
  color: ${plannerColors.muted};
  font-size: 0.82rem;
`;

const SongsControlGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const SongTemplateList = styled.div`
  display: grid;
  gap: 0.55rem;
  margin-top: 0.75rem;
`;

const SongTemplateCard = styled.div`
  border: 1px solid rgba(229, 211, 179, 0.2);
  border-radius: 14px;
  background:
    radial-gradient(circle at 0% 0%, rgba(229, 211, 179, 0.12), transparent 38%),
    linear-gradient(155deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.02));
  padding: 0.78rem;
  display: grid;
  grid-template-columns: 68px 1fr;
  gap: 0.8rem;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const SongTemplateCover = styled.div`
  width: 68px;
  height: 68px;
  border-radius: 12px;
  border: 1px solid rgba(229, 211, 179, 0.28);
  background:
    radial-gradient(circle at 25% 20%, rgba(229, 211, 179, 0.35), transparent 46%),
    linear-gradient(140deg, #303035 0%, #1f1f22 52%, #17171a 100%);
  color: ${plannerColors.accent};
  font-size: 1rem;
  font-weight: 800;
  letter-spacing: 0.07em;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SongTemplateBody = styled.div`
  display: grid;
  gap: 0.5rem;
`;

const SongTemplateTitle = styled.h4`
  margin: 0;
  color: ${plannerColors.text};
  font-size: 1rem;
  font-weight: 800;
  letter-spacing: -0.01em;
`;

const SongTemplateMeta = styled.p`
  margin: 0;
  color: ${plannerColors.accent};
  font-size: 0.8rem;
  font-weight: 600;
`;

const SongTemplateChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
`;

const SongTemplateChip = styled.span`
  border-radius: 999px;
  border: 1px solid rgba(229, 211, 179, 0.26);
  background: rgba(255, 255, 255, 0.03);
  color: ${plannerColors.text};
  font-size: 0.72rem;
  line-height: 1;
  padding: 0.3rem 0.5rem;
`;

const SelectInput = styled.select`
  border: 1px solid ${plannerColors.border};
  border-radius: 12px;
  background: ${plannerColors.surfaceHover};
  color: ${plannerColors.text};
  padding: 0.62rem 0.72rem;
  transition: border-color 140ms ease, box-shadow 140ms ease, background-color 140ms ease;

  &:focus {
    outline: none;
    border-color: rgba(229, 211, 179, 0.5);
    box-shadow: 0 0 0 3px rgba(229, 211, 179, 0.14);
  }
`;

function firstRelation<T>(value: T | T[] | null | undefined): T | null {
  if (!value) return null;
  return Array.isArray(value) ? value[0] ?? null : value;
}

function formatWhen(value: string) {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatSongLastScheduledDate(value?: string | null) {
  if (!value) return 'Not scheduled';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function overlapsServiceDate(serviceDate: string, startsAt: string, endsAt: string): boolean {
  const serviceMs = new Date(serviceDate).getTime();
  const startMs = new Date(startsAt).getTime();
  const endMs = new Date(endsAt).getTime();
  if (Number.isNaN(serviceMs) || Number.isNaN(startMs) || Number.isNaN(endMs)) return false;
  return startMs <= serviceMs && endMs > serviceMs;
}

function getAssignmentTone(status: string | null | undefined): 'ok' | 'warn' | 'bad' {
  const normalized = normalizePlannerStatus(status);
  if (normalized === 'unavailable') return 'bad';
  if (normalized === 'maybe') return 'warn';
  return 'ok';
}

function normalizedRoleKeys(input: unknown): string[] {
  if (!Array.isArray(input)) return [];
  return input
    .filter((role): role is string => typeof role === 'string')
    .map((role) => role.trim().toLowerCase())
    .filter(Boolean);
}

function getSubsectionDurationMinutes(subsection: PlannerSubsection): number {
  if (subsection.length_minutes != null && subsection.length_minutes >= 0) {
    return subsection.length_minutes;
  }

  const display = subsection.length_display?.trim();
  if (!display) return 0;

  const minuteOnly = display.match(/^(\d+)\s*(min|mins|minute|minutes)?$/i);
  if (minuteOnly) {
    return Number.parseInt(minuteOnly[1], 10) || 0;
  }

  const minuteSecond = display.match(/^(\d+):(\d{1,2})$/);
  if (minuteSecond) {
    const minutes = Number.parseInt(minuteSecond[1], 10) || 0;
    const seconds = Number.parseInt(minuteSecond[2], 10) || 0;
    return minutes + (seconds / 60);
  }

  return 0;
}

function formatPlannerDuration(durationMinutes: number): string {
  if (!Number.isFinite(durationMinutes) || durationMinutes <= 0) return 'No duration';

  const totalSeconds = Math.max(0, Math.round(durationMinutes * 60));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    if (seconds > 0) return `${hours}h ${minutes}m ${seconds}s`;
    if (minutes > 0) return `${hours}h ${minutes}m`;
    return `${hours}h`;
  }

  if (seconds > 0) {
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
  }

  return `${minutes} min`;
}

function parseTimeslotLength(rawValue: string): {
  minutes: number | null;
  display: string | null;
  error: string | null;
} {
  const trimmed = rawValue.trim();
  if (!trimmed) {
    return { minutes: null, display: null, error: null };
  }

  const minuteOnly = trimmed.match(/^\d+$/);
  if (minuteOnly) {
    const minutes = Number.parseInt(trimmed, 10);
    if (!Number.isFinite(minutes) || minutes < 0) {
      return {
        minutes: null,
        display: null,
        error: 'Length must be a positive number of minutes.',
      };
    }

    return {
      minutes,
      display: `${minutes} min`,
      error: null,
    };
  }

  const minuteSecond = trimmed.match(/^(\d+):(\d{1,2})$/);
  if (minuteSecond) {
    const minutes = Number.parseInt(minuteSecond[1], 10);
    const seconds = Number.parseInt(minuteSecond[2], 10);
    if (!Number.isFinite(minutes) || minutes < 0 || !Number.isFinite(seconds) || seconds < 0 || seconds > 59) {
      return {
        minutes: null,
        display: null,
        error: 'Use mm:ss with seconds between 00 and 59.',
      };
    }

    return {
      minutes,
      display: `${minutes}:${String(seconds).padStart(2, '0')}`,
      error: null,
    };
  }

  return {
    minutes: null,
    display: null,
    error: 'Length format must be minutes (10) or mm:ss (9:30).',
  };
}

function personInitial(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return '?';
  return trimmed.charAt(0).toUpperCase();
}

function songCoverInitials(title: string, artist?: string | null): string {
  const source = (title || artist || '').trim();
  if (!source) return 'SP';
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
}

function ensureUniqueSubsectionName(baseName: string, existingNames: string[]): string {
  const normalizedBase = baseName.trim();
  if (!normalizedBase) return baseName;

  const existing = new Set(
    existingNames
      .map((name) => name.trim().toLowerCase())
      .filter(Boolean)
  );

  if (!existing.has(normalizedBase.toLowerCase())) {
    return normalizedBase;
  }

  let suffix = 2;
  while (existing.has(`${normalizedBase} (${suffix})`.toLowerCase())) {
    suffix += 1;
  }
  return `${normalizedBase} (${suffix})`;
}

export default function ChurchPlannerServiceDetailsPage() {
  const navigate = useNavigate();
  const { serviceId } = useParams<{ serviceId: string }>();
  const { user } = useAuth();
  const planner = useChurchPlanner(true, user?.id);
  const rolesQuery = usePlannerRoles(true);
  const [activeTab, setActiveTab] = useState<'Order' | 'Teams' | 'Files' | 'Songs'>('Order');
  const [serviceMenuOpen, setServiceMenuOpen] = useState(false);
  const [deletingServicePlan, setDeletingServicePlan] = useState(false);
  const [sectionMenuId, setSectionMenuId] = useState<string | null>(null);
  const [timeslotMenuId, setTimeslotMenuId] = useState<string | null>(null);
  const [editSectionId, setEditSectionId] = useState<string | null>(null);
  const [editSectionName, setEditSectionName] = useState('');
  const [editServiceOpen, setEditServiceOpen] = useState(false);
  const [editServiceTitle, setEditServiceTitle] = useState('');
  const [editServiceDate, setEditServiceDate] = useState('');
  const [editServiceTime, setEditServiceTime] = useState('');
  const [editServiceStartTime, setEditServiceStartTime] = useState('');
  const [editServiceAutoAssignEnabled, setEditServiceAutoAssignEnabled] = useState(false);
  const [editServiceLocation, setEditServiceLocation] = useState('');
  const [editServiceNotes, setEditServiceNotes] = useState('');
  const [selectedEditServiceTemplateId, setSelectedEditServiceTemplateId] = useState('');
  const [loadingEditServiceTemplate, setLoadingEditServiceTemplate] = useState(false);
  const [teamPositionOpen, setTeamPositionOpen] = useState(false);
  const [newRoleArea, setNewRoleArea] = useState('');
  const [newRoleName, setNewRoleName] = useState('');
  const [addSectionOpen, setAddSectionOpen] = useState(false);
  const [newSectionName, setNewSectionName] = useState('');
  const [selectedSectionTitle, setSelectedSectionTitle] = useState('');
  const [isCustomSectionTitle, setIsCustomSectionTitle] = useState(false);
  const [addTimeslotOpen, setAddTimeslotOpen] = useState(false);
  const [newTimeslotSectionId, setNewTimeslotSectionId] = useState('');
  const [newTimeslotName, setNewTimeslotName] = useState('');
  const [newTimeslotDescription, setNewTimeslotDescription] = useState('');
  const [newTimeslotLength, setNewTimeslotLength] = useState('');
  const [newTimeslotSongTemplateId, setNewTimeslotSongTemplateId] = useState('');
  const [newTimeslotSongSearch, setNewTimeslotSongSearch] = useState('');
  const [creatingTimeslotSongTemplate, setCreatingTimeslotSongTemplate] = useState(false);
  const [newTimeslotTaggedMemberIds, setNewTimeslotTaggedMemberIds] = useState<string[]>([]);
  const [editTimeslotId, setEditTimeslotId] = useState<string | null>(null);
  const [editTimeslotName, setEditTimeslotName] = useState('');
  const [editTimeslotDescription, setEditTimeslotDescription] = useState('');
  const [editTimeslotLength, setEditTimeslotLength] = useState('');
  const [editTimeslotSongTemplateId, setEditTimeslotSongTemplateId] = useState('');
  const [editTimeslotSongSearch, setEditTimeslotSongSearch] = useState('');
  const [editTimeslotTaggedMemberIds, setEditTimeslotTaggedMemberIds] = useState<string[]>([]);
  const [draggedTimeslotId, setDraggedTimeslotId] = useState<string | null>(null);
  const [draggedTimeslotSectionId, setDraggedTimeslotSectionId] = useState<string | null>(null);
  const [dragOverTimeslotId, setDragOverTimeslotId] = useState<string | null>(null);
  const [dropPendingTimeslotId, setDropPendingTimeslotId] = useState<string | null>(null);
  const [taggedMemberCache, setTaggedMemberCache] = useState<Record<string, AssignableMember>>({});
  const [pickerMembers, setPickerMembers] = useState<AssignableMember[]>([]);
  const [pickerSearch, setPickerSearch] = useState('');
  const [pickerLoading, setPickerLoading] = useState(false);
  const [teamPickerRoleId, setTeamPickerRoleId] = useState<string | null>(null);
  const [teamMemberSearch, setTeamMemberSearch] = useState('');
  const [teamAssignmentRoleId, setTeamAssignmentRoleId] = useState<string | null>(null);
  const pickerRequestSeq = useRef(0);
  const [songsLoading, setSongsLoading] = useState(false);
  const [songsSaving, setSongsSaving] = useState(false);
  const [applyingSongId, setApplyingSongId] = useState<string | null>(null);
  const [songsSectionId, setSongsSectionId] = useState<string>('');
  const [serviceSongSearch, setServiceSongSearch] = useState('');
  const [songTemplates, setSongTemplates] = useState<SongTemplate[]>([]);
  const [editingSongTemplateId, setEditingSongTemplateId] = useState<string | null>(null);
  const [editSongTitle, setEditSongTitle] = useState('');
  const [editSongArtist, setEditSongArtist] = useState('');
  const [editSongKey, setEditSongKey] = useState('');
  const [editSongLength, setEditSongLength] = useState('5');
  const [editSongBpm, setEditSongBpm] = useState('');
  const [editSongSequence, setEditSongSequence] = useState('0');
  const [editSongNotes, setEditSongNotes] = useState('');
  const [editSongLyricsUrl, setEditSongLyricsUrl] = useState('');
  const [editSongChordUrl, setEditSongChordUrl] = useState('');

  const plan = useMemo(
    () => planner.plans.find((entry) => entry.id === serviceId) || null,
    [planner.plans, serviceId]
  );

  const selectedNewTimeslotSong = useMemo(
    () => songTemplates.find((song) => song.id === newTimeslotSongTemplateId) || null,
    [songTemplates, newTimeslotSongTemplateId]
  );

  const filteredTimeslotSongTemplates = useMemo(() => {
    const term = newTimeslotSongSearch.trim().toLowerCase();
    if (!term) return songTemplates;
    return songTemplates.filter((song) => {
      const title = song.title?.toLowerCase() || '';
      const artist = song.artist?.toLowerCase() || '';
      return title.includes(term) || artist.includes(term);
    });
  }, [songTemplates, newTimeslotSongSearch]);

  const timeslotSongSelectOptions = useMemo(() => {
    if (!newTimeslotSongTemplateId) return filteredTimeslotSongTemplates;
    const containsSelected = filteredTimeslotSongTemplates.some((song) => song.id === newTimeslotSongTemplateId);
    if (containsSelected) return filteredTimeslotSongTemplates;
    const selected = songTemplates.find((song) => song.id === newTimeslotSongTemplateId);
    return selected ? [selected, ...filteredTimeslotSongTemplates] : filteredTimeslotSongTemplates;
  }, [filteredTimeslotSongTemplates, newTimeslotSongTemplateId, songTemplates]);

  const selectedEditTimeslotSong = useMemo(
    () => songTemplates.find((song) => song.id === editTimeslotSongTemplateId) || null,
    [songTemplates, editTimeslotSongTemplateId]
  );

  const filteredEditTimeslotSongTemplates = useMemo(() => {
    const term = editTimeslotSongSearch.trim().toLowerCase();
    if (!term) return songTemplates;
    return songTemplates.filter((song) => {
      const title = song.title?.toLowerCase() || '';
      const artist = song.artist?.toLowerCase() || '';
      return title.includes(term) || artist.includes(term);
    });
  }, [songTemplates, editTimeslotSongSearch]);

  const editTimeslotSongSelectOptions = useMemo(() => {
    if (!editTimeslotSongTemplateId) return filteredEditTimeslotSongTemplates;
    const containsSelected = filteredEditTimeslotSongTemplates.some((song) => song.id === editTimeslotSongTemplateId);
    if (containsSelected) return filteredEditTimeslotSongTemplates;
    const selected = songTemplates.find((song) => song.id === editTimeslotSongTemplateId);
    return selected ? [selected, ...filteredEditTimeslotSongTemplates] : filteredEditTimeslotSongTemplates;
  }, [editTimeslotSongTemplateId, filteredEditTimeslotSongTemplates, songTemplates]);

  const selectedNewTimeslotTaggedMembers = useMemo(() => {
    if (!newTimeslotTaggedMemberIds.length) return [] as AssignableMember[];
    const memberMap = new Map<string, AssignableMember>();
    Object.values(taggedMemberCache).forEach((member) => {
      memberMap.set(member.id, member);
    });
    pickerMembers.forEach((member) => {
      memberMap.set(member.id, member);
    });
    newTimeslotTaggedMemberIds.forEach((memberId) => {
      if (!memberMap.has(memberId)) {
        const fallbackMember = plan?.service_plan_subsection_assignments
          ?.map((assignment) => firstRelation(assignment.assigned_user))
          .find((assigned) => assigned?.id === memberId);
        if (fallbackMember) {
          memberMap.set(memberId, {
            id: memberId,
            full_name: fallbackMember.full_name || null,
            email: fallbackMember.email || null,
            title: fallbackMember.title || null,
            avatar: fallbackMember.avatar || null,
          });
        }
      }
    });
    return newTimeslotTaggedMemberIds
      .map((memberId) => memberMap.get(memberId))
      .filter((member): member is AssignableMember => Boolean(member));
  }, [newTimeslotTaggedMemberIds, pickerMembers, plan?.service_plan_subsection_assignments, taggedMemberCache]);

  const selectedEditTimeslotTaggedMembers = useMemo(() => {
    if (!editTimeslotTaggedMemberIds.length) return [] as AssignableMember[];
    const memberMap = new Map<string, AssignableMember>();
    Object.values(taggedMemberCache).forEach((member) => {
      memberMap.set(member.id, member);
    });
    pickerMembers.forEach((member) => {
      memberMap.set(member.id, member);
    });
    editTimeslotTaggedMemberIds.forEach((memberId) => {
      if (!memberMap.has(memberId)) {
        const fallbackMember = plan?.service_plan_subsection_assignments
          ?.map((assignment) => firstRelation(assignment.assigned_user))
          .find((assigned) => assigned?.id === memberId);
        if (fallbackMember) {
          memberMap.set(memberId, {
            id: memberId,
            full_name: fallbackMember.full_name || null,
            email: fallbackMember.email || null,
            title: fallbackMember.title || null,
            avatar: fallbackMember.avatar || null,
          });
        }
      }
    });
    return editTimeslotTaggedMemberIds
      .map((memberId) => memberMap.get(memberId))
      .filter((member): member is AssignableMember => Boolean(member));
  }, [editTimeslotTaggedMemberIds, pickerMembers, plan?.service_plan_subsection_assignments, taggedMemberCache]);

  useEffect(() => {
    if (!pickerMembers.length) return;
    setTaggedMemberCache((current) => {
      let changed = false;
      const next = { ...current };
      pickerMembers.forEach((member) => {
        const existing = next[member.id];
        if (
          !existing ||
          existing.full_name !== member.full_name ||
          existing.email !== member.email ||
          existing.title !== member.title ||
          existing.avatar !== member.avatar
        ) {
          next[member.id] = member;
          changed = true;
        }
      });
      return changed ? next : current;
    });
  }, [pickerMembers]);

  const editingTimeslotAssignments = useMemo(() => {
    if (!editTimeslotId) return [] as PlannerSubsectionAssignment[];
    return (plan?.service_plan_subsection_assignments || []).filter(
      (assignment) => assignment.subsection_id === editTimeslotId
    );
  }, [editTimeslotId, plan?.service_plan_subsection_assignments]);

  const songTemplateById = useMemo(() => {
    const map = new Map<string, SongTemplate>();
    songTemplates.forEach((song) => {
      map.set(song.id, song);
    });
    return map;
  }, [songTemplates]);

  const serviceSongTemplates = useMemo(() => {
    const linkedTemplateIds = new Set<string>();
    (plan?.service_plan_subsections || []).forEach((subsection) => {
      if (subsection.song_template_id) {
        linkedTemplateIds.add(subsection.song_template_id);
      }
    });

    return Array.from(linkedTemplateIds)
      .map((templateId) => songTemplateById.get(templateId))
      .filter((song): song is SongTemplate => Boolean(song))
      .sort(compareSongTemplatesByTitle);
  }, [plan?.service_plan_subsections, songTemplateById]);

  const searchedSongTemplates = useMemo(() => {
    const term = serviceSongSearch.trim().toLowerCase();
    if (!term) return [] as SongTemplate[];

    return songTemplates
      .filter((song) => {
        const title = (song.title || '').toLowerCase();
        const artist = (song.artist || '').toLowerCase();
        const keySignature = (song.key_signature || '').toLowerCase();
        const notes = (song.notes || '').toLowerCase();
        return (
          title.includes(term)
          || artist.includes(term)
          || keySignature.includes(term)
          || notes.includes(term)
        );
      })
      .sort(compareSongTemplatesByTitle)
      .slice(0, 20);
  }, [serviceSongSearch, songTemplates]);

  const sections = useMemo(
    () => [...(plan?.service_plan_sections || [])].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
    [plan?.service_plan_sections]
  );

  useEffect(() => {
    if (!sections.length) {
      setSongsSectionId('');
      return;
    }
    setSongsSectionId((current) => (current && sections.some((section) => section.id === current) ? current : sections[0].id));
  }, [sections]);

  const subsectionsBySection = useMemo(() => {
    const map = new Map<string, PlannerSubsection[]>();
    (plan?.service_plan_subsections || []).forEach((subsection) => {
      const key = subsection.section_id;
      const list = map.get(key) || [];
      list.push(subsection);
      map.set(key, list);
    });

    map.forEach((list, key) => {
      list.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      map.set(key, list);
    });

    return map;
  }, [plan?.service_plan_subsections]);

  const subsectionAssignments = useMemo(
    () => plan?.service_plan_subsection_assignments || [],
    [plan?.service_plan_subsection_assignments]
  );

  const assignmentsBySubsection = useMemo(() => {
    const map = new Map<string, PlannerSubsectionAssignment[]>();
    subsectionAssignments.forEach((assignment) => {
      const key = assignment.subsection_id;
      const list = map.get(key) || [];
      list.push(assignment);
      map.set(key, list);
    });
    return map;
  }, [subsectionAssignments]);

  const roleAssignmentsByRole = useMemo(() => {
    const map = new Map<string, PlannerAssignment>();
    (plan?.service_plan_assignments || []).forEach((assignment) => {
      if (!assignment.role_id || map.has(assignment.role_id)) return;
      map.set(assignment.role_id, assignment);
    });
    return map;
  }, [plan?.service_plan_assignments]);

  const allRolesForGrouping = useMemo(() => {
    const map = new Map<string, PlannerRole>();
    (rolesQuery.data || []).forEach((role) => {
      if (!role.id) return;
      map.set(role.id, role);
    });

    (plan?.service_plan_assignments || []).forEach((assignment) => {
      const role = firstRelation(assignment.service_plan_roles);
      if (!role?.id || map.has(role.id)) return;
      map.set(role.id, role);
    });

    return Array.from(map.values());
  }, [plan?.service_plan_assignments, rolesQuery.data]);

  const groupedRoles = useMemo(() => {
    const map = new Map<string, { label: string; teamId: string | null; roles: PlannerRole[] }>();
    allRolesForGrouping.forEach((role) => {
      const group = getPlannerRoleTeamGroup(role);
      const entry = map.get(group.key) ?? {
        label: group.label,
        teamId: group.teamId,
        roles: [],
      };
      entry.roles.push(role);
      map.set(group.key, entry);
    });

    return Array.from(map.entries())
      .map(([id, entry]) => ({
        id,
        label: entry.label,
        teamId: entry.teamId,
        roles: [...entry.roles].sort((a, b) => a.name.localeCompare(b.name)),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allRolesForGrouping]);

  const groupedFiles = useMemo(() => {
    const map = new Map<string, NonNullable<typeof plan>['service_plan_media_items']>();
    (plan?.service_plan_media_items || []).forEach((item) => {
      const key = (item.category || 'other').trim().toLowerCase();
      const list = map.get(key) || [];
      list.push(item);
      map.set(key, list);
    });

    return Array.from(map.entries())
      .map(([category, items]) => ({
        category,
        items: [...(items || [])].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
      }))
      .sort((a, b) => a.category.localeCompare(b.category));
  }, [plan?.service_plan_media_items]);

  const currentUserRoles = useMemo(
    () => normalizedRoleKeys(planner.currentUserQuery.data?.roles ?? user?.user_metadata?.roles),
    [planner.currentUserQuery.data?.roles, user?.user_metadata?.roles]
  );

  const canManageTeamAssignments =
    hasAnyPermission(planner.currentUserQuery.data?.roles ?? user?.user_metadata?.roles, [
      Permission.MANAGE_CHURCH_PLAN_ASSIGNMENTS,
      Permission.MANAGE_CHURCH_PLANNER_ROLES,
    ]) ||
    currentUserRoles.includes('service_planner_team_leader') ||
    currentUserRoles.includes('leader');

  const canManageAllPlannerTeams = hasAnyPermission(
    planner.currentUserQuery.data?.roles ?? user?.user_metadata?.roles,
    [Permission.MANAGE_CHURCH_PLAN_ASSIGNMENTS, Permission.MANAGE_CHURCH_PLANNER_ROLES]
  ) || currentUserRoles.includes('leader');

  const canManageSongAssignments = hasAnyPermission(
    planner.currentUserQuery.data?.roles ?? user?.user_metadata?.roles,
    [Permission.MANAGE_CHURCH_PLAN_ASSIGNMENTS, Permission.MANAGE_CHURCH_PLANNER_ROLES]
  );

  const canCreatePlans = hasAnyPermission(
    planner.currentUserQuery.data?.roles ?? user?.user_metadata?.roles,
    [Permission.CREATE_CHURCH_PLAN]
  );

  const canViewServiceMenu =
    currentUserRoles.includes('service_planner_admin')
    || currentUserRoles.includes('admin')
    || currentUserRoles.includes('super_admin');

  const currentUserManagedTeamIds = useMemo(() => {
    if (canManageAllPlannerTeams) {
      return PLANNER_TEAM_DEFINITIONS.map((team) => team.id);
    }

    if (!user?.id) return [] as string[];

    return Array.from(
      new Set(
        (planner.teamManagersQuery.data ?? [])
          .filter((row) => row.user_id === user.id)
          .map((row) => row.team_id)
          .filter(Boolean)
      )
    );
  }, [canManageAllPlannerTeams, planner.teamManagersQuery.data, user?.id]);

  const currentUserManagedTeamIdSet = useMemo(
    () => new Set(currentUserManagedTeamIds),
    [currentUserManagedTeamIds]
  );

  const visibleGroupedRoles = useMemo(() => {
    if (canViewServiceMenu || canManageAllPlannerTeams) return groupedRoles;
    return groupedRoles.filter((group) =>
      group.teamId
        ? currentUserManagedTeamIdSet.has(group.teamId)
        : group.roles.some((role) => {
          const teamId = resolveTeamIdFromRole(role);
          return Boolean(teamId && currentUserManagedTeamIdSet.has(teamId));
        })
    );
  }, [canManageAllPlannerTeams, canViewServiceMenu, currentUserManagedTeamIdSet, groupedRoles]);

  useEffect(() => {
    if (!canViewServiceMenu && serviceMenuOpen) {
      setServiceMenuOpen(false);
    }
    if (!canViewServiceMenu && sectionMenuId) {
      setSectionMenuId(null);
    }
    if (!canViewServiceMenu && timeslotMenuId) {
      setTimeslotMenuId(null);
    }
  }, [canViewServiceMenu, sectionMenuId, serviceMenuOpen, timeslotMenuId]);

  const activeTeamPickerRole = useMemo(
    () => (rolesQuery.data || []).find((role) => role.id === teamPickerRoleId) || null,
    [rolesQuery.data, teamPickerRoleId]
  );

  const teamPickerMembers = useMemo(() => {
    const memberMap = new Map<string, AssignableMember>();
    pickerMembers.forEach((member) => {
      memberMap.set(member.id, member);
    });
    (plan?.service_plan_assignments || []).forEach((assignment) => {
      const assigned = firstRelation(assignment.assigned_user);
      const id = assigned?.id || assignment.user_id;
      if (!id || memberMap.has(id)) return;
      memberMap.set(id, {
        id,
        full_name: assigned?.full_name || null,
        email: assigned?.email || null,
        title: assigned?.title || null,
        avatar: assigned?.avatar || null,
      });
    });

    const term = teamMemberSearch.trim().toLowerCase();
    return Array.from(memberMap.values())
      .filter((member) => {
        if (!term) return true;
        const fullName = member.full_name?.toLowerCase() || '';
        const email = member.email?.toLowerCase() || '';
        return fullName.includes(term) || email.includes(term);
      })
      .sort((a, b) => {
        const aName = (a.full_name || a.email || a.id).toLowerCase();
        const bName = (b.full_name || b.email || b.id).toLowerCase();
        return aName.localeCompare(bName);
      });
  }, [pickerMembers, plan?.service_plan_assignments, teamMemberSearch]);

  const serviceAvailabilityByUserId = useMemo(() => {
    const map = new Map<string, string>();
    if (!plan?.id) return map;

    (planner.availability || []).forEach((entry) => {
      if (entry.service_plan_id !== plan.id || !entry.user_id) return;
      map.set(entry.user_id, String(entry.availability_status || '').trim().toLowerCase());
    });

    return map;
  }, [plan?.id, planner.availability]);

  const calendarUnavailableByUserId = useMemo(() => {
    const map = new Map<string, boolean>();
    if (!plan?.service_date) return map;

    (planner.memberUnavailability || []).forEach((entry) => {
      if (!entry.user_id) return;
      if (!overlapsServiceDate(plan.service_date, entry.starts_at, entry.ends_at)) return;
      map.set(entry.user_id, true);
    });

    return map;
  }, [plan?.service_date, planner.memberUnavailability]);

  const getAvailabilityBlockReasonForUserId = useCallback((userId: string | null | undefined): string | null => {
    if (!plan || !userId) return null;

    const serviceAvailability = serviceAvailabilityByUserId.get(userId) || '';
    if (serviceAvailability === 'unavailable') {
      return 'Marked unavailable for this service.';
    }

    if (calendarUnavailableByUserId.get(userId)) {
      return 'Blocked by calendar unavailability range.';
    }

    return null;
  }, [calendarUnavailableByUserId, plan, serviceAvailabilityByUserId]);

  const getTeamMemberBlockReason = useCallback((member: AssignableMember): string | null => {
    return getAvailabilityBlockReasonForUserId(member.id);
  }, [getAvailabilityBlockReasonForUserId]);

  const timelineSections = useMemo(() => {
    return sections.map((section) => {
      const sectionSubsections = (subsectionsBySection.get(section.id) || []).map((subsection) => ({
        subsection,
        durationMinutes: getSubsectionDurationMinutes(subsection),
      }));

      return {
        section,
        subsections: sectionSubsections,
        sectionDurationMinutes: sectionSubsections.reduce((sum, current) => sum + current.durationMinutes, 0),
      };
    });
  }, [sections, subsectionsBySection]);

  const serviceDurationMinutes = useMemo(
    () => timelineSections.reduce((sum, section) => sum + section.sectionDurationMinutes, 0),
    [timelineSections]
  );

  const timelineProgress = useMemo(() => {
    if (!plan?.service_date || !plan?.start_time || !timelineSections.length) {
      return {
        activeSectionIndex: -1,
        activeSubsectionId: null as string | null,
        activeSectionProgress: 0,
      };
    }

    const [hhRaw, mmRaw] = plan.start_time.split(':');
    const hh = Number.parseInt(hhRaw || '', 10);
    const mm = Number.parseInt(mmRaw || '', 10);
    if (!Number.isFinite(hh) || !Number.isFinite(mm)) {
      return { activeSectionIndex: -1, activeSubsectionId: null, activeSectionProgress: 0 };
    }

    const start = new Date(plan.service_date);
    start.setHours(hh, mm, 0, 0);
    const now = new Date();
    const elapsedMinutes = (now.getTime() - start.getTime()) / 60000;
    if (!Number.isFinite(elapsedMinutes) || elapsedMinutes < 0) {
      return { activeSectionIndex: -1, activeSubsectionId: null, activeSectionProgress: 0 };
    }

    if (serviceDurationMinutes <= 0) {
      return { activeSectionIndex: 0, activeSubsectionId: timelineSections[0]?.subsections[0]?.subsection.id || null, activeSectionProgress: 0 };
    }

    let elapsedCursor = elapsedMinutes;
    let activeSectionIndex = timelineSections.length - 1;
    const lastSectionSubsections = timelineSections[timelineSections.length - 1]?.subsections || [];
    let activeSubsectionId: string | null =
      lastSectionSubsections[lastSectionSubsections.length - 1]?.subsection.id || null;
    let activeSectionProgress = 1;

    for (let sectionIndex = 0; sectionIndex < timelineSections.length; sectionIndex += 1) {
      const section = timelineSections[sectionIndex];
      const sectionDuration = section.sectionDurationMinutes;
      if (elapsedCursor <= sectionDuration) {
        activeSectionIndex = sectionIndex;
        if (sectionDuration > 0) {
          activeSectionProgress = Math.max(0, Math.min(1, elapsedCursor / sectionDuration));
        } else {
          activeSectionProgress = 1;
        }

        let subsectionCursor = elapsedCursor;
        activeSubsectionId = section.subsections[0]?.subsection.id || null;
        for (let subsectionIndex = 0; subsectionIndex < section.subsections.length; subsectionIndex += 1) {
          const currentSubsection = section.subsections[subsectionIndex];
          if (subsectionCursor <= currentSubsection.durationMinutes || subsectionIndex === section.subsections.length - 1) {
            activeSubsectionId = currentSubsection.subsection.id;
            break;
          }
          subsectionCursor -= currentSubsection.durationMinutes;
        }
        break;
      }
      elapsedCursor -= sectionDuration;
    }

    return { activeSectionIndex, activeSubsectionId, activeSectionProgress };
  }, [plan?.service_date, plan?.start_time, serviceDurationMinutes, timelineSections]);

  const loadPickerMembers = useCallback(async (searchTerm: string) => {
    const requestSeq = pickerRequestSeq.current + 1;
    pickerRequestSeq.current = requestSeq;
    setPickerLoading(true);
    const term = searchTerm.trim();
    let plannerQuery = supabase
      .from('users_directory')
      .select('id, full_name, email, title, avatar, roles')
      .overlaps('roles', ['service_planner', 'service_planner_admin'])
      .order('full_name', { ascending: true })
      .limit(50);

    if (term) {
      plannerQuery = plannerQuery.or(`full_name.ilike.%${term}%,email.ilike.%${term}%`);
    }

    const { data, error } = await plannerQuery;

    if (requestSeq !== pickerRequestSeq.current) return;

    if (error) {
      setPickerLoading(false);
      window.alert(error.message || 'Unable to load planner members.');
      return;
    }

    if (requestSeq !== pickerRequestSeq.current) return;
    setPickerMembers((data as AssignableMember[]) || []);
    setPickerLoading(false);
  }, []);

  useEffect(() => {
    if (!editTimeslotId && !addTimeslotOpen) return;
    const handle = window.setTimeout(() => {
      void loadPickerMembers(pickerSearch);
    }, 220);
    return () => window.clearTimeout(handle);
  }, [editTimeslotId, addTimeslotOpen, pickerSearch, loadPickerMembers]);

  useEffect(() => {
    if (!teamPickerRoleId) return;
    const handle = window.setTimeout(() => {
      void loadPickerMembers(teamMemberSearch);
    }, 220);
    return () => window.clearTimeout(handle);
  }, [teamPickerRoleId, teamMemberSearch, loadPickerMembers]);

  const loadSongTemplates = async () => {
    setSongsLoading(true);
    const { data, error } = await supabase
      .from('service_song_templates')
      .select('*')
      .eq('is_active', true)
      .order('title', { ascending: true });

    setSongsLoading(false);
    if (error) {
      window.alert(error.message || 'Unable to load song templates.');
      return;
    }

    setSongTemplates([...((data as SongTemplate[]) || [])].sort(compareSongTemplatesByTitle));
  };

  useEffect(() => {
    void loadSongTemplates();
  }, []);

  const closeTimeslotEditor = () => {
    setEditTimeslotId(null);
    setEditTimeslotName('');
    setEditTimeslotDescription('');
    setEditTimeslotLength('');
    setEditTimeslotSongTemplateId('');
    setEditTimeslotSongSearch('');
    setEditTimeslotTaggedMemberIds([]);
    setTaggedMemberCache({});
    setPickerSearch('');
    setPickerMembers([]);
  };

  const refetchPlans = planner.plansQuery.refetch;

  const refreshPlans = useCallback(async () => {
    await refetchPlans();
  }, [refetchPlans]);

  const openEditService = () => {
    if (!plan) return;
    const serviceDate = new Date(plan.service_date);
    const yyyy = serviceDate.getFullYear();
    const mm = String(serviceDate.getMonth() + 1).padStart(2, '0');
    const dd = String(serviceDate.getDate()).padStart(2, '0');
    const hh = String(serviceDate.getHours()).padStart(2, '0');
    const min = String(serviceDate.getMinutes()).padStart(2, '0');

    setEditServiceTitle(plan.title || '');
    setEditServiceDate(`${yyyy}-${mm}-${dd}`);
    setEditServiceTime(`${hh}:${min}`);
    setEditServiceStartTime(plan.start_time ? plan.start_time.slice(0, 5) : `${hh}:${min}`);
    setEditServiceAutoAssignEnabled(Boolean(plan.auto_assign_enabled));
    setEditServiceLocation(plan.location || '');
    setEditServiceNotes(plan.notes || '');
    setSelectedEditServiceTemplateId('');
    setEditServiceOpen(true);
    setServiceMenuOpen(false);
  };

  const handleLoadServiceTemplate = async () => {
    if (!plan) return;
    if (!selectedEditServiceTemplateId) {
      window.alert('Select a service template first.');
      return;
    }

    setLoadingEditServiceTemplate(true);
    try {
      await planner.applyServiceTemplate({
        service_plan_id: plan.id,
        template_id: selectedEditServiceTemplateId,
      });
      setSelectedEditServiceTemplateId('');
      await refreshPlans();
      window.alert('Service template loaded.');
    } catch (error) {
      window.alert(error instanceof Error ? error.message : 'Unable to load service template.');
    } finally {
      setLoadingEditServiceTemplate(false);
    }
  };

  const handleUpdateService = async () => {
    if (!plan) return;
    if (!editServiceTitle.trim() || !editServiceDate.trim() || !editServiceTime.trim()) {
      window.alert('Title, date, and time are required.');
      return;
    }

    const nextServiceDate = new Date(`${editServiceDate.trim()}T${editServiceTime.trim()}:00`);
    if (Number.isNaN(nextServiceDate.getTime())) {
      window.alert('Invalid date or time.');
      return;
    }

    const { error } = await supabase
      .from('service_plans')
      .update({
        title: editServiceTitle.trim(),
        service_date: nextServiceDate.toISOString(),
        start_time: editServiceStartTime.trim() ? `${editServiceStartTime.trim()}:00` : null,
        auto_assign_enabled: editServiceAutoAssignEnabled,
        location: editServiceLocation.trim() || null,
        notes: editServiceNotes.trim() || null,
      })
      .eq('id', plan.id);

    if (error) {
      window.alert(error.message || 'Unable to update service.');
      return;
    }

    if (editServiceAutoAssignEnabled) {
      try {
        await planner.autoAssignServicePlan(plan.id);
      } catch (autoAssignError) {
        console.error('Planner auto-assign failed after service update:', autoAssignError);
      }
    }

    setEditServiceOpen(false);
    await refreshPlans();
  };

  const handleDeleteService = async () => {
    if (!plan) return;

    const confirmed = window.confirm(
      `Delete "${plan.title}" on ${formatWhen(plan.service_date)}?\n\nThis action cannot be undone.`
    );
    if (!confirmed) return;

    setServiceMenuOpen(false);
    setDeletingServicePlan(true);

    try {
      await planner.deletePlan(plan.id);
      navigate('/church-planner/schedule', { replace: true });
    } catch (error) {
      window.alert(error instanceof Error ? error.message : 'Unable to delete service plan.');
    } finally {
      setDeletingServicePlan(false);
    }
  };

  const openDuplicateAcrossDates = () => {
    if (!plan || !canCreatePlans) return;
    setServiceMenuOpen(false);
    navigate(`/church-planner/services/new?sourcePlanId=${encodeURIComponent(plan.id)}`);
  };

  const openTeamPositionComposer = () => {
    setNewRoleArea('');
    setNewRoleName('');
    setTeamPositionOpen(true);
    setServiceMenuOpen(false);
  };

  const handleAddTeamPosition = async () => {
    if (!newRoleArea.trim() || !newRoleName.trim()) {
      window.alert('Team area and position name are required.');
      return;
    }

    try {
      await planner.createRole({
        area: newRoleArea.trim().toLowerCase().replace(/\s+/g, '_'),
        name: newRoleName.trim(),
        is_active: true,
      });
      setTeamPositionOpen(false);
      await refreshPlans();
    } catch (error) {
      window.alert(error instanceof Error ? error.message : 'Unable to add team position.');
    }
  };

  const openTeamMemberPicker = (roleId: string) => {
    if (!canManageTeamAssignments) {
      window.alert('Only planner admins and team leaders can assign members.');
      return;
    }
    setTeamMemberSearch('');
    setTeamPickerRoleId(roleId);
    void loadPickerMembers('');
  };

  const handleAssignTeamRole = async (roleId: string, userId: string | null) => {
    if (!plan) return;
    if (!canManageTeamAssignments) {
      window.alert('Only planner admins and team leaders can assign members.');
      return;
    }

    const existing = roleAssignmentsByRole.get(roleId);
    if (!existing && !userId) {
      setTeamPickerRoleId(null);
      return;
    }

    if (userId) {
      const targetMember = teamPickerMembers.find((member) => member.id === userId)
        || pickerMembers.find((member) => member.id === userId)
        || null;
      if (targetMember) {
        const blockReason = getTeamMemberBlockReason(targetMember);
        if (blockReason) {
          window.alert(blockReason);
          return;
        }
      }
    }

    setTeamAssignmentRoleId(roleId);
    try {
      if (existing?.id) {
        await planner.updateAssignment({
          assignmentId: existing.id,
          updates: {
            user_id: userId,
            status: userId ? existing.status || 'scheduled' : 'scheduled',
          },
        });
      } else if (userId) {
        await planner.createAssignment({
          service_plan_id: plan.id,
          role_id: roleId,
          user_id: userId,
          status: 'scheduled',
        });
      }

      setTeamPickerRoleId(null);
      await refreshPlans();
    } catch (error) {
      window.alert(error instanceof Error ? error.message : 'Unable to update team assignment.');
    } finally {
      setTeamAssignmentRoleId(null);
    }
  };

  const openAddSection = () => {
    setSectionMenuId(null);
    setNewSectionName('');
    setSelectedSectionTitle('');
    setIsCustomSectionTitle(false);
    setAddSectionOpen(true);
    setServiceMenuOpen(false);
  };

  const openEditSection = (sectionId: string, sectionName: string) => {
    setSectionMenuId(null);
    setEditSectionId(sectionId);
    setEditSectionName(sectionName);
  };

  const closeEditSection = () => {
    setEditSectionId(null);
    setEditSectionName('');
  };

  const handleUpdateSection = async () => {
    if (!plan || !editSectionId) return;
    const nextName = editSectionName.trim();
    if (!nextName) {
      window.alert('Section name is required.');
      return;
    }

    const duplicate = sections.some(
      (section) =>
        section.id !== editSectionId
        && section.name.trim().toLowerCase() === nextName.toLowerCase()
    );
    if (duplicate) {
      window.alert('A section with that name already exists in this service.');
      return;
    }

    try {
      await planner.renameSection({
        service_plan_id: plan.id,
        section_id: editSectionId,
        name: nextName,
      });
      closeEditSection();
      await refreshPlans();
    } catch (error) {
      window.alert(error instanceof Error ? error.message : 'Unable to rename section.');
    }
  };

  const handleDeleteSection = async (sectionId: string, sectionName: string) => {
    if (!plan) return;
    setSectionMenuId(null);

    const shouldDelete = window.confirm(
      `Delete section "${sectionName}"? This removes all timeslots and order items in that section.`
    );
    if (!shouldDelete) return;

    try {
      await planner.deleteSection({
        service_plan_id: plan.id,
        section_id: sectionId,
      });
      if (editSectionId === sectionId) {
        closeEditSection();
      }
      await refreshPlans();
    } catch (error) {
      window.alert(error instanceof Error ? error.message : 'Unable to delete section.');
    }
  };

  const handleSaveServiceTemplate = async () => {
    if (!plan) return;
    setServiceMenuOpen(false);

    const suggestedName = `${plan.title} Template`;
    const name = window.prompt('Template name', suggestedName)?.trim();
    if (!name) return;

    const description = window.prompt('Template description (optional)', '')?.trim() || null;

    try {
      await planner.saveServiceTemplate({
        service_plan_id: plan.id,
        name,
        description,
        created_by: user?.id || null,
      });
      window.alert('Service template saved.');
    } catch (error) {
      window.alert(error instanceof Error ? error.message : 'Unable to save service template.');
    }
  };

  const openAddTimeslot = (sectionId?: string) => {
    if (!plan) return;
    if (!sections.length) {
      window.alert('Add a section first before creating timeslots.');
      return;
    }

    setNewTimeslotSectionId(sectionId || sections[0].id);
    setNewTimeslotName('');
    setNewTimeslotDescription('');
    setNewTimeslotLength('');
    setNewTimeslotSongTemplateId('');
    setNewTimeslotSongSearch('');
    setNewTimeslotTaggedMemberIds([]);
    setTaggedMemberCache({});
    setPickerSearch('');
    setPickerMembers([]);
    setAddTimeslotOpen(true);
    setServiceMenuOpen(false);
    void loadPickerMembers('');
  };

  const handleSelectNewTimeslotSongTemplate = (nextSongId: string) => {
    setNewTimeslotSongTemplateId(nextSongId);
    const selectedSong = songTemplates.find((song) => song.id === nextSongId);
    if (!selectedSong) return;
    if (!newTimeslotName.trim()) {
      setNewTimeslotName(selectedSong.title);
    }
    if (!newTimeslotDescription.trim() && selectedSong.notes) {
      setNewTimeslotDescription(selectedSong.notes);
    }
    if (!newTimeslotLength.trim()) {
      setNewTimeslotLength(formatSongTemplateLengthDisplay(selectedSong) || '');
    }
  };

  const handleNewTimeslotSongSearchChange = (nextSearch: string) => {
    setNewTimeslotSongSearch(nextSearch);
    const matchedSong = findAutoMatchedSongTemplate(songTemplates, nextSearch);
    if (matchedSong && matchedSong.id !== newTimeslotSongTemplateId) {
      handleSelectNewTimeslotSongTemplate(matchedSong.id);
    }
  };

  const handleQuickCreateTimeslotSongTemplate = async () => {
    const title = newTimeslotSongSearch.trim();
    if (!title) {
      window.alert('Type a song title first.');
      return;
    }

    const existing = songTemplates.find((song) => (song.title || '').trim().toLowerCase() === title.toLowerCase());
    if (existing) {
      handleSelectNewTimeslotSongTemplate(existing.id);
      return;
    }

    const parsedDuration = newTimeslotLength.trim()
      ? parseSongTemplateDurationInput(newTimeslotLength)
      : { value: { lengthMinutes: 5, lengthSeconds: 300 }, error: null };
    if (!parsedDuration.value) {
      window.alert(parsedDuration.error || 'Duration must be minutes (5) or m:ss (4:53).');
      return;
    }
    const nextSequence = songTemplates.reduce((max, song) => {
      const value = Number.isFinite(song.sequence) ? song.sequence : 0;
      return Math.max(max, value);
    }, 0) + 1;

    setCreatingTimeslotSongTemplate(true);
    const { data, error } = await supabase
      .from('service_song_templates')
      .insert({
        title,
        artist: null,
        key_signature: null,
        length_minutes: parsedDuration.value.lengthMinutes,
        length_seconds: parsedDuration.value.lengthSeconds,
        bpm: null,
        sequence: nextSequence,
        notes: newTimeslotDescription.trim() || null,
        lyrics_url: null,
        chord_chart_url: null,
        is_active: true,
        created_by: user?.id || null,
      })
      .select('*')
      .single();
    setCreatingTimeslotSongTemplate(false);

    if (error || !data) {
      window.alert(error?.message || 'Unable to create song template.');
      return;
    }

    const createdSong = data as SongTemplate;
    setSongTemplates((current) =>
      [...current, createdSong].sort(compareSongTemplatesByTitle)
    );
    handleSelectNewTimeslotSongTemplate(createdSong.id);
    setNewTimeslotSongSearch('');
  };

  const handleSelectEditTimeslotSongTemplate = (nextSongId: string) => {
    setEditTimeslotSongTemplateId(nextSongId);
    const selectedSong = songTemplates.find((song) => song.id === nextSongId);
    if (!selectedSong) return;
    if (!editTimeslotName.trim()) {
      setEditTimeslotName(selectedSong.title);
    }
    if (!editTimeslotDescription.trim() && selectedSong.notes) {
      setEditTimeslotDescription(selectedSong.notes);
    }
    if (!editTimeslotLength.trim()) {
      setEditTimeslotLength(formatSongTemplateLengthDisplay(selectedSong) || '');
    }
  };

  const handleEditTimeslotSongSearchChange = (nextSearch: string) => {
    setEditTimeslotSongSearch(nextSearch);
    const matchedSong = findAutoMatchedSongTemplate(songTemplates, nextSearch);
    if (matchedSong && matchedSong.id !== editTimeslotSongTemplateId) {
      handleSelectEditTimeslotSongTemplate(matchedSong.id);
    }
  };

  const handleQuickCreateEditTimeslotSongTemplate = async () => {
    const title = editTimeslotSongSearch.trim();
    if (!title) {
      window.alert('Type a song title first.');
      return;
    }

    const existing = songTemplates.find((song) => (song.title || '').trim().toLowerCase() === title.toLowerCase());
    if (existing) {
      handleSelectEditTimeslotSongTemplate(existing.id);
      return;
    }

    const parsedDuration = editTimeslotLength.trim()
      ? parseSongTemplateDurationInput(editTimeslotLength)
      : { value: { lengthMinutes: 5, lengthSeconds: 300 }, error: null };
    if (!parsedDuration.value) {
      window.alert(parsedDuration.error || 'Duration must be minutes (5) or m:ss (4:53).');
      return;
    }
    const nextSequence = songTemplates.reduce((max, song) => {
      const value = Number.isFinite(song.sequence) ? song.sequence : 0;
      return Math.max(max, value);
    }, 0) + 1;

    setCreatingTimeslotSongTemplate(true);
    const { data, error } = await supabase
      .from('service_song_templates')
      .insert({
        title,
        artist: null,
        key_signature: null,
        length_minutes: parsedDuration.value.lengthMinutes,
        length_seconds: parsedDuration.value.lengthSeconds,
        bpm: null,
        sequence: nextSequence,
        notes: editTimeslotDescription.trim() || null,
        lyrics_url: null,
        chord_chart_url: null,
        is_active: true,
        created_by: user?.id || null,
      })
      .select('*')
      .single();
    setCreatingTimeslotSongTemplate(false);

    if (error || !data) {
      window.alert(error?.message || 'Unable to create song template.');
      return;
    }

    const createdSong = data as SongTemplate;
    setSongTemplates((current) =>
      [...current, createdSong].sort(compareSongTemplatesByTitle)
    );
    handleSelectEditTimeslotSongTemplate(createdSong.id);
    setEditTimeslotSongSearch('');
  };

  const handleAddSection = async () => {
    const resolvedSectionName = isCustomSectionTitle
      ? newSectionName.trim()
      : selectedSectionTitle.trim();

    if (!plan || !resolvedSectionName) {
      window.alert('Section name is required.');
      return;
    }

    const alreadyExists = sections.some(
      (section) => section.name.trim().toLowerCase() === resolvedSectionName.toLowerCase()
    );
    if (alreadyExists) {
      window.alert('This section already exists in the service.');
      return;
    }

    const { error } = await supabase
      .from('service_plan_sections')
      .insert({
        service_plan_id: plan.id,
        name: resolvedSectionName,
        sort_order: sections.length,
      });

    if (error) {
      window.alert(error.message || 'Unable to add section.');
      return;
    }

    setAddSectionOpen(false);
    setSelectedSectionTitle('');
    setIsCustomSectionTitle(false);
    setNewSectionName('');
    await refreshPlans();
  };

  const handleAddTimeslot = async () => {
    if (!plan) return;
    if (!newTimeslotSectionId) {
      window.alert('Select a section for this timeslot.');
      return;
    }
    const fallbackLength = formatSongTemplateLengthDisplay(selectedNewTimeslotSong);
    const resolvedName = newTimeslotName.trim() || selectedNewTimeslotSong?.title?.trim() || '';
    const resolvedDescription =
      newTimeslotDescription.trim() || selectedNewTimeslotSong?.notes?.trim() || null;
    const resolvedLengthRaw = newTimeslotLength.trim() || fallbackLength || '';

    if (!resolvedName) {
      window.alert('Timeslot name is required.');
      return;
    }

    const parsedLength = parseTimeslotLength(resolvedLengthRaw);
    if (parsedLength.error) {
      window.alert(parsedLength.error);
      return;
    }

    const sectionSubsections = (subsectionsBySection.get(newTimeslotSectionId) || []).sort(
      (a, b) => (a.sort_order || 0) - (b.sort_order || 0)
    );
    const uniqueName = ensureUniqueSubsectionName(
      resolvedName,
      sectionSubsections.map((subsection) => subsection.name || '')
    );
    const maxSortOrder = sectionSubsections.length
      ? (sectionSubsections[sectionSubsections.length - 1].sort_order || 0)
      : -1;

    const { data, error } = await supabase
      .from('service_plan_subsections')
      .insert({
        service_plan_id: plan.id,
        section_id: newTimeslotSectionId,
        song_template_id: selectedNewTimeslotSong?.id || null,
        name: uniqueName,
        description: resolvedDescription,
        length_minutes: parsedLength.minutes,
        length_display: parsedLength.display,
        sort_order: maxSortOrder + 1,
      })
      .select('id')
      .single();

    if (error) {
      window.alert(error.message || 'Unable to add timeslot.');
      return;
    }

    if (data?.id && newTimeslotTaggedMemberIds.length) {
      const blockedMemberTag = newTimeslotTaggedMemberIds
        .map((memberId) => ({ memberId, reason: getAvailabilityBlockReasonForUserId(memberId) }))
        .find((entry): entry is { memberId: string; reason: string } => Boolean(entry.reason));
      if (blockedMemberTag) {
        window.alert(blockedMemberTag.reason);
      }

      try {
        await Promise.all(
          newTimeslotTaggedMemberIds
            .filter((memberId) => !getAvailabilityBlockReasonForUserId(memberId))
            .map((memberId) =>
            planner.createSubsectionAssignment({
              service_plan_id: plan.id,
              subsection_id: data.id,
              user_id: memberId,
              status: 'pending',
            })
            )
        );
      } catch (assignmentError) {
        window.alert(
          assignmentError instanceof Error
            ? assignmentError.message
            : 'Timeslot was created, but one or more member tags could not be added.'
        );
      }
    }

    setAddTimeslotOpen(false);
    setNewTimeslotSongTemplateId('');
    setNewTimeslotSongSearch('');
    setNewTimeslotTaggedMemberIds([]);
    setTaggedMemberCache({});
    setPickerSearch('');
    setPickerMembers([]);
    await refreshPlans();
  };

  const openTimeslotEditor = async (subsection: PlannerSubsection) => {
    setTimeslotMenuId(null);
    setEditTimeslotId(subsection.id);
    setEditTimeslotName(subsection.name || '');
    setEditTimeslotDescription(subsection.description || '');
    setEditTimeslotLength(
      subsection.length_minutes != null ? String(subsection.length_minutes) : ''
    );
    setEditTimeslotSongTemplateId(subsection.song_template_id || '');
    setEditTimeslotSongSearch('');
    const existingTaggedMemberIds = (plan?.service_plan_subsection_assignments || [])
      .filter((assignment) => assignment.subsection_id === subsection.id)
      .map((assignment) => assignment.user_id)
      .filter((memberId): memberId is string => Boolean(memberId));
    setEditTimeslotTaggedMemberIds(Array.from(new Set(existingTaggedMemberIds)));
    setPickerSearch('');
    await loadPickerMembers('');
  };

  const resetTimeslotDragState = useCallback(() => {
    setDraggedTimeslotId(null);
    setDraggedTimeslotSectionId(null);
    setDragOverTimeslotId(null);
  }, []);

  const handleTimeslotDragStart = useCallback(
    (sectionId: string, subsectionId: string) => (event: React.DragEvent<HTMLElement>) => {
      if (!canViewServiceMenu || planner.isMutating) {
        event.preventDefault();
        return;
      }

      setTimeslotMenuId(null);
      setDraggedTimeslotSectionId(sectionId);
      setDraggedTimeslotId(subsectionId);
      setDragOverTimeslotId(subsectionId);
      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', subsectionId);
      }
    },
    [canViewServiceMenu, planner.isMutating]
  );

  const handleTimeslotDragOver = useCallback(
    (sectionId: string, subsectionId: string) => (event: React.DragEvent<HTMLElement>) => {
      if (!draggedTimeslotId || draggedTimeslotSectionId !== sectionId || planner.isMutating) {
        return;
      }

      event.preventDefault();
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'move';
      }
      if (dragOverTimeslotId !== subsectionId) {
        setDragOverTimeslotId(subsectionId);
      }
    },
    [dragOverTimeslotId, draggedTimeslotId, draggedTimeslotSectionId, planner.isMutating]
  );

  const handleTimeslotDrop = useCallback(
    (sectionId: string, sectionSubsections: TimelineSubsection[], targetSubsectionId: string) =>
      async (event: React.DragEvent<HTMLElement>) => {
        event.preventDefault();

        if (!plan || !draggedTimeslotId || draggedTimeslotSectionId !== sectionId || planner.isMutating) {
          resetTimeslotDragState();
          return;
        }

        const directions = buildTimeslotDragDirections(
          sectionSubsections,
          draggedTimeslotId,
          targetSubsectionId,
          (item) => item.subsection.id
        );

        if (!directions.length) {
          resetTimeslotDragState();
          return;
        }

        const subsectionId = draggedTimeslotId;
        setDropPendingTimeslotId(subsectionId);
        resetTimeslotDragState();

        try {
          for (const direction of directions) {
            await planner.reorderSubsection({
              service_plan_id: plan.id,
              section_id: sectionId,
              subsection_id: subsectionId,
              direction,
            });
          }
          await refreshPlans();
        } catch (error) {
          window.alert(error instanceof Error ? error.message : 'Unable to move timeslot.');
        } finally {
          setDropPendingTimeslotId(null);
        }
      },
    [draggedTimeslotId, draggedTimeslotSectionId, plan, planner, refreshPlans, resetTimeslotDragState]
  );

  const handleReorderTimeslot = async (
    sectionId: string,
    subsectionId: string,
    direction: 'up' | 'down'
  ) => {
    if (!plan) return;
    setTimeslotMenuId(null);

    try {
      await planner.reorderSubsection({
        service_plan_id: plan.id,
        section_id: sectionId,
        subsection_id: subsectionId,
        direction,
      });
      await refreshPlans();
    } catch (error) {
      window.alert(error instanceof Error ? error.message : 'Unable to move timeslot.');
    }
  };

  const handleDeleteTimeslot = async (sectionId: string, subsection: PlannerSubsection) => {
    if (!plan) return;
    setTimeslotMenuId(null);

    const shouldDelete = window.confirm(
      `Delete timeslot "${subsection.name}"? This also removes order items inside this timeslot.`
    );
    if (!shouldDelete) return;

    try {
      await planner.deleteSubsection({
        service_plan_id: plan.id,
        section_id: sectionId,
        subsection_id: subsection.id,
      });
      await refreshPlans();
    } catch (error) {
      window.alert(error instanceof Error ? error.message : 'Unable to delete timeslot.');
    }
  };

  const handleUpdateTimeslot = async () => {
    if (!editTimeslotId || !editTimeslotName.trim()) {
      window.alert('Timeslot name is required.');
      return;
    }

    const parsedLength = parseTimeslotLength(editTimeslotLength);
    if (parsedLength.error) {
      window.alert(parsedLength.error);
      return;
    }

    const { error } = await supabase
      .from('service_plan_subsections')
      .update({
        name: editTimeslotName.trim(),
        description: editTimeslotDescription.trim() || null,
        song_template_id: selectedEditTimeslotSong?.id || null,
        length_minutes: parsedLength.minutes,
        length_display: parsedLength.display,
      })
      .eq('id', editTimeslotId);

    if (error) {
      window.alert(error.message || 'Unable to update timeslot.');
      return;
    }

    if (plan && editTimeslotId) {
      const existingByUserId = new Map<string, PlannerSubsectionAssignment>();
      editingTimeslotAssignments.forEach((assignment) => {
        if (!assignment.user_id || existingByUserId.has(assignment.user_id)) return;
        existingByUserId.set(assignment.user_id, assignment);
      });

      const nextTaggedIds = Array.from(new Set(editTimeslotTaggedMemberIds));
      const toCreate = nextTaggedIds.filter((memberId) => !existingByUserId.has(memberId));
      const toDelete = editingTimeslotAssignments.filter(
        (assignment) => assignment.user_id && !nextTaggedIds.includes(assignment.user_id)
      );
      const blockedMemberTag = toCreate
        .map((memberId) => ({ memberId, reason: getAvailabilityBlockReasonForUserId(memberId) }))
        .find((entry): entry is { memberId: string; reason: string } => Boolean(entry.reason));

      if (toCreate.length || toDelete.length) {
        if (blockedMemberTag) {
          window.alert(blockedMemberTag.reason);
        }
        try {
          await Promise.all([
            ...toCreate
              .filter((memberId) => !getAvailabilityBlockReasonForUserId(memberId))
              .map((memberId) =>
              planner.createSubsectionAssignment({
                service_plan_id: plan.id,
                subsection_id: editTimeslotId,
                user_id: memberId,
                status: 'pending',
              })
              ),
            ...toDelete.map((assignment) => planner.deleteSubsectionAssignment(assignment.id)),
          ]);
        } catch (assignmentError) {
          window.alert(
            assignmentError instanceof Error
              ? assignmentError.message
              : 'Timeslot was updated, but one or more member tags could not be synced.'
          );
        }
      }
    }

    await refreshPlans();
    closeTimeslotEditor();
  };

  const handleRemoveSubsectionAssignment = async (assignmentId: string) => {
    try {
      await planner.deleteSubsectionAssignment(assignmentId);
      await refreshPlans();
    } catch (error) {
      window.alert(error instanceof Error ? error.message : 'Unable to remove tag.');
    }
  };

  const handleDeleteSongTemplate = async (songId: string) => {
    const { error } = await supabase
      .from('service_song_templates')
      .delete()
      .eq('id', songId);

    if (error) {
      window.alert(error.message || 'Unable to delete song template.');
      return;
    }

    await loadSongTemplates();
  };

  const startInlineSongTemplateEdit = (song: SongTemplate) => {
    setEditingSongTemplateId(song.id);
    setEditSongTitle(song.title || '');
    setEditSongArtist(song.artist || '');
    setEditSongKey(song.key_signature || '');
    setEditSongLength(String(song.length_minutes ?? 0));
    setEditSongBpm(song.bpm != null ? String(song.bpm) : '');
    setEditSongSequence(String(song.sequence ?? 0));
    setEditSongNotes(song.notes || '');
    setEditSongLyricsUrl(song.lyrics_url || '');
    setEditSongChordUrl(song.chord_chart_url || '');
  };

  const cancelInlineSongTemplateEdit = () => {
    setEditingSongTemplateId(null);
    setEditSongTitle('');
    setEditSongArtist('');
    setEditSongKey('');
    setEditSongLength('5');
    setEditSongBpm('');
    setEditSongSequence('0');
    setEditSongNotes('');
    setEditSongLyricsUrl('');
    setEditSongChordUrl('');
  };

  const handleUpdateSongTemplate = async () => {
    if (!editingSongTemplateId) return;

    const title = editSongTitle.trim();
    const lengthMinutes = Number.parseInt(editSongLength.trim(), 10);
    const sequence = Number.parseInt(editSongSequence.trim(), 10);
    const bpm = editSongBpm.trim() ? Number.parseInt(editSongBpm.trim(), 10) : null;

    if (!title) {
      window.alert('Song title is required.');
      return;
    }
    if (!Number.isFinite(lengthMinutes) || lengthMinutes < 0) {
      window.alert('Length must be a number greater than or equal to 0.');
      return;
    }
    if (!Number.isFinite(sequence) || sequence < 0) {
      window.alert('Sequence must be a number greater than or equal to 0.');
      return;
    }
    if (bpm != null && (!Number.isFinite(bpm) || bpm <= 0)) {
      window.alert('BPM must be a number greater than 0.');
      return;
    }

    setSongsSaving(true);
    const { error } = await supabase
      .from('service_song_templates')
      .update({
        title,
        artist: editSongArtist.trim() || null,
        key_signature: editSongKey.trim() || null,
        length_minutes: lengthMinutes,
        bpm,
        sequence,
        notes: editSongNotes.trim() || null,
        lyrics_url: editSongLyricsUrl.trim() || null,
        chord_chart_url: editSongChordUrl.trim() || null,
      })
      .eq('id', editingSongTemplateId);
    setSongsSaving(false);

    if (error) {
      window.alert(error.message || 'Unable to update song template.');
      return;
    }

    cancelInlineSongTemplateEdit();
    await loadSongTemplates();
  };

  const handleApplySongTemplate = async (song: SongTemplate) => {
    if (!plan || !songsSectionId) {
      window.alert('Select a section first.');
      return;
    }

    const currentSectionSubsections = (subsectionsBySection.get(songsSectionId) || []).sort(
      (a, b) => (a.sort_order || 0) - (b.sort_order || 0)
    );
    const uniqueName = ensureUniqueSubsectionName(
      song.title,
      currentSectionSubsections.map((subsection) => subsection.name || '')
    );
    const maxSortOrder = currentSectionSubsections.length
      ? (currentSectionSubsections[currentSectionSubsections.length - 1].sort_order || 0)
      : -1;

    setApplyingSongId(song.id);
    const { error } = await supabase
      .from('service_plan_subsections')
      .insert({
        service_plan_id: plan.id,
        section_id: songsSectionId,
        song_template_id: song.id,
        name: uniqueName,
        description: song.notes || null,
        length_minutes: song.length_minutes,
        length_display: formatSongTemplateLengthDisplay(song),
        sort_order: maxSortOrder + 1,
      });
    setApplyingSongId(null);

    if (error) {
      window.alert(error.message || 'Unable to add song to this service.');
      return;
    }

    await refreshPlans();
  };

  if (planner.isLoading || rolesQuery.isLoading) {
    return (
      <PlannerPage>
        <PlannerCard>
          <PlannerHeading>Service Details</PlannerHeading>
          <PlannerSubtle>Loading service details...</PlannerSubtle>
        </PlannerCard>
      </PlannerPage>
    );
  }

  if (planner.isError || rolesQuery.isError) {
    return (
      <PlannerPage>
        <PlannerCard>
          <PlannerHeading>Service Details</PlannerHeading>
          <PlannerError>Unable to load service details right now.</PlannerError>
        </PlannerCard>
      </PlannerPage>
    );
  }

  if (!plan) {
    return (
      <PlannerPage>
        <PlannerCard>
          <HeaderRow>
            <PlannerHeading>Service Details</PlannerHeading>
            <BackButton onClick={() => navigate('/church-planner/schedule')}>Back to Schedule</BackButton>
          </HeaderRow>
          <PlannerSubtle>Service not found.</PlannerSubtle>
        </PlannerCard>
      </PlannerPage>
    );
  }

  return (
    <PlannerPage>
      <HeroCard>
        <HeaderRow>
          <div>
            <PlannerKicker>Service Planner</PlannerKicker>
            <PlannerHeading>{plan.title}</PlannerHeading>
            <HeroMetaRow>
              <HeroMetaPill>{formatWhen(plan.service_date)}</HeroMetaPill>
              <HeroMetaPill>{plan.location || 'Location not set'}</HeroMetaPill>
              {plan.start_time ? <HeroMetaPill>Start {plan.start_time.slice(0, 5)}</HeroMetaPill> : null}
              <HeroMetaPill>Total {formatPlannerDuration(serviceDurationMinutes)}</HeroMetaPill>
            </HeroMetaRow>
          </div>
          <HeaderActions>
            {canViewServiceMenu ? (
              <MenuWrap>
                <MenuButton onClick={() => setServiceMenuOpen((value) => !value)}>...</MenuButton>
                {serviceMenuOpen ? (
                  <MenuPanel>
                    <MenuItem onClick={openEditService}>Edit Service</MenuItem>
                    <MenuItem onClick={openTeamPositionComposer}>Add Team Position</MenuItem>
                    <MenuItem onClick={openAddSection}>Add Section</MenuItem>
                    <MenuItem onClick={() => openAddTimeslot()}>Add Timeslot</MenuItem>
                    <MenuItem onClick={handleSaveServiceTemplate}>Save Service Template</MenuItem>
                    {canCreatePlans ? <MenuItem onClick={openDuplicateAcrossDates}>Duplicate Across Dates</MenuItem> : null}
                    <MenuItem $danger onClick={() => void handleDeleteService()} disabled={deletingServicePlan}>
                      {deletingServicePlan ? 'Deleting...' : 'Delete Service'}
                    </MenuItem>
                  </MenuPanel>
                ) : null}
              </MenuWrap>
            ) : null}
            <BackButton onClick={() => navigate('/church-planner/schedule')}>Back to Schedule</BackButton>
          </HeaderActions>
        </HeaderRow>
        <TabStrip>
          <TabRow>
            {(['Order', 'Teams', 'Files', 'Songs'] as const).map((tab) => (
              <TabButton key={tab} $active={activeTab === tab} onClick={() => setActiveTab(tab)}>
                {tab}
              </TabButton>
            ))}
          </TabRow>
        </TabStrip>
      </HeroCard>

      {activeTab === 'Order' ? (
        <PlannerCard>
          <PlannerHeading>Order of Service</PlannerHeading>

          {sections.length === 0 ? (
            <>
              <PlannerSubtle>No sections found for this service.</PlannerSubtle>
              <ButtonRow style={{ justifyContent: 'flex-start', marginTop: '0.6rem' }}>
                <ActionButton onClick={openAddSection}>Add First Section</ActionButton>
              </ButtonRow>
            </>
          ) : (
            <SectionList>
              {timelineSections.map((timelineSection, sectionIndex) => {
                const sectionSubsections = timelineSection.subsections;
                const displayedSectionSubsections =
                  draggedTimeslotId
                  && draggedTimeslotSectionId === timelineSection.section.id
                  && dragOverTimeslotId
                    ? reorderDraggedTimeslots(
                      sectionSubsections,
                      draggedTimeslotId,
                      dragOverTimeslotId,
                      (item) => item.subsection.id
                    )
                    : sectionSubsections;
                const sectionState: 'complete' | 'active' | 'upcoming' =
                  sectionIndex < timelineProgress.activeSectionIndex
                    ? 'complete'
                    : sectionIndex === timelineProgress.activeSectionIndex
                      ? 'active'
                      : 'upcoming';
                const connectorFillPct =
                  sectionState === 'complete'
                    ? 100
                    : sectionState === 'active'
                      ? timelineProgress.activeSectionProgress * 100
                      : 0;

                return (
                  <StaggerItem key={timelineSection.section.id} $delayMs={Math.min(sectionIndex * 55, 320)}>
                    <TimelineSectionRow>
                    <TimelineRail>
                      <TimelineNode $state={sectionState} />
                      {sectionIndex < timelineSections.length - 1 ? (
                        <TimelineConnector>
                          <TimelineConnectorFill $heightPct={connectorFillPct} />
                        </TimelineConnector>
                      ) : null}
                    </TimelineRail>
                    <SectionCard>
                      <SectionHeader>
                        <SubsectionTitle>{timelineSection.section.name}</SubsectionTitle>
                        <SectionHeaderMeta>
                          <SectionBadge>{sectionSubsections.length} slots</SectionBadge>
                          {canViewServiceMenu ? (
                            <MenuWrap>
                              <MenuButton
                                onClick={() =>
                                  setSectionMenuId((value) =>
                                    value === timelineSection.section.id ? null : timelineSection.section.id
                                  )
                                }
                              >
                                ...
                              </MenuButton>
                              {sectionMenuId === timelineSection.section.id ? (
                                <MenuPanel>
                                  <MenuItem
                                    disabled={planner.isMutating}
                                    onClick={() => openEditSection(timelineSection.section.id, timelineSection.section.name)}
                                  >
                                    Edit Section
                                  </MenuItem>
                                  <MenuItem
                                    $danger
                                    disabled={planner.isMutating}
                                    onClick={() => void handleDeleteSection(timelineSection.section.id, timelineSection.section.name)}
                                  >
                                    Delete Section
                                  </MenuItem>
                                </MenuPanel>
                              ) : null}
                            </MenuWrap>
                          ) : null}
                        </SectionHeaderMeta>
                      </SectionHeader>
                      {sectionSubsections.length === 0 ? (
                        <EmptyHint>No timeslots yet.</EmptyHint>
                      ) : (
                        displayedSectionSubsections.map(({ subsection }, subsectionIndex) => {
                          const tags = assignmentsBySubsection.get(subsection.id) || [];
                          const linkedSong = subsection.song_template_id ? songTemplateById.get(subsection.song_template_id) || null : null;
                          const subsectionDescription = subsection.description?.trim() || '';
                          const linkedSongNotes = linkedSong?.notes?.trim() || '';
                          const showTimeslotNotes = !(subsectionDescription && linkedSongNotes && subsectionDescription === linkedSongNotes);
                          const isActiveSubsection = timelineProgress.activeSubsectionId === subsection.id;
                          const TimeslotWrapper = isActiveSubsection ? ActiveSubsectionRow : SubsectionRow;
                          const originalIndex = sectionSubsections.findIndex((item) => item.subsection.id === subsection.id);
                          const canMoveUp = originalIndex > 0;
                          const canMoveDown = originalIndex > -1 && originalIndex < sectionSubsections.length - 1;
                          const isDraggingSubsection = draggedTimeslotId === subsection.id;
                          const isDropTarget = dragOverTimeslotId === subsection.id && draggedTimeslotId !== subsection.id;
                          const canDragTimeslot = canViewServiceMenu && !planner.isMutating && dropPendingTimeslotId == null;
                          const durationLabel =
                            subsection.length_display ||
                            (subsection.length_minutes != null ? `${subsection.length_minutes} min` : 'No duration');
                          return (
                            <TimeslotWrapper
                              key={subsection.id}
                              $canDrag={canDragTimeslot}
                              $isDragging={isDraggingSubsection || dropPendingTimeslotId === subsection.id}
                              $isDropTarget={isDropTarget}
                              onDragOver={handleTimeslotDragOver(timelineSection.section.id, subsection.id)}
                              onDrop={handleTimeslotDrop(timelineSection.section.id, sectionSubsections, subsection.id)}
                            >
                              <SubsectionDurationColumn>
                                <SubsectionDurationLabel>DUR</SubsectionDurationLabel>
                                <SubsectionDurationPill>{durationLabel}</SubsectionDurationPill>
                              </SubsectionDurationColumn>
                              <SubsectionContent>
                                <SubsectionBodyRow>
                                  <SubsectionHeadingGroup>
                                    {canViewServiceMenu ? (
                                      <TimeslotDragHandle
                                        $disabled={!canDragTimeslot}
                                        draggable={canDragTimeslot}
                                        onDragStart={handleTimeslotDragStart(timelineSection.section.id, subsection.id)}
                                        onDragEnd={resetTimeslotDragState}
                                        aria-label={`Drag to reorder ${subsection.name}`}
                                        title="Drag to reorder within this section"
                                      >
                                        ::
                                      </TimeslotDragHandle>
                                    ) : null}
                                    <div>
                                      <SubsectionTitle>{subsection.name}</SubsectionTitle>
                                    </div>
                                  </SubsectionHeadingGroup>
                                  {canViewServiceMenu ? (
                                    <MenuWrap>
                                      <MenuButton onClick={() => setTimeslotMenuId((value) => (value === subsection.id ? null : subsection.id))}>...</MenuButton>
                                      {timeslotMenuId === subsection.id ? (
                                        <MenuPanel>
                                          <MenuItem
                                            disabled={!canMoveUp || planner.isMutating}
                                            onClick={() => void handleReorderTimeslot(timelineSection.section.id, subsection.id, 'up')}
                                          >
                                            Move Up
                                          </MenuItem>
                                          <MenuItem
                                            disabled={!canMoveDown || planner.isMutating}
                                            onClick={() => void handleReorderTimeslot(timelineSection.section.id, subsection.id, 'down')}
                                          >
                                            Move Down
                                          </MenuItem>
                                          <MenuItem onClick={() => void openTimeslotEditor(subsection)}>Edit Timeslot</MenuItem>
                                          <MenuItem
                                            $danger
                                            disabled={planner.isMutating}
                                            onClick={() => void handleDeleteTimeslot(timelineSection.section.id, subsection)}
                                          >
                                            Delete Timeslot
                                          </MenuItem>
                                        </MenuPanel>
                                      ) : null}
                                    </MenuWrap>
                                  ) : null}
                                </SubsectionBodyRow>
                                {showTimeslotNotes ? (
                                  subsection.description ? (
                                    <TimeslotDescription>{subsection.description}</TimeslotDescription>
                                  ) : (
                                    <TimeslotDescription>No notes provided.</TimeslotDescription>
                                  )
                                ) : null}
                                {linkedSong ? (
                                  <TimeslotSongLink
                                    type="button"
                                    onClick={() => navigate(`/church-planner/songs/${linkedSong.id}`)}
                                    aria-label={`Open song ${linkedSong.title}`}
                                  >
                                    <TimeslotSongTitle>{linkedSong.title}</TimeslotSongTitle>
                                    <TimeslotSongMetaRow>
                                      <TimeslotSongMetaChip>{linkedSong.artist || 'Unknown artist'}</TimeslotSongMetaChip>
                                      <TimeslotSongMetaChip>{linkedSong.key_signature || 'No key'}</TimeslotSongMetaChip>
                                      <TimeslotSongMetaChip>
                                        {linkedSong.bpm != null ? `${linkedSong.bpm} BPM` : '-- BPM'}
                                      </TimeslotSongMetaChip>
                                    </TimeslotSongMetaRow>
                                  </TimeslotSongLink>
                                ) : null}
                                {tags.length > 0 && (
                                  <ChipRow>
                                    {tags.map((tag) => {
                                      const assignedUser = firstRelation(tag.assigned_user);
                                      const label = assignedUser?.full_name || assignedUser?.email || tag.user_id;
                                      const avatar = assignedUser?.avatar || null;
                                      return (
                                        <PersonChip key={tag.id}>
                                          {avatar ? (
                                            <PersonAvatar
                                              src={avatar}
                                              alt={label}
                                              loading="lazy"
                                              referrerPolicy="no-referrer"
                                            />
                                          ) : (
                                            <PersonAvatarFallback>{personInitial(label)}</PersonAvatarFallback>
                                          )}
                                          <PersonChipName title={label}>{label}</PersonChipName>
                                          <RemoveChipButton
                                            aria-label="Remove person tag"
                                            onClick={() => void handleRemoveSubsectionAssignment(tag.id)}
                                          >
                                            x
                                          </RemoveChipButton>
                                        </PersonChip>
                                      );
                                    })}
                                  </ChipRow>
                                )}
                              </SubsectionContent>
                            </TimeslotWrapper>
                          );
                        })
                      )}
                      <SectionDurationFooter>
                        <SectionDurationLabel>Section Total</SectionDurationLabel>
                        <SectionDurationValue>{formatPlannerDuration(timelineSection.sectionDurationMinutes)}</SectionDurationValue>
                      </SectionDurationFooter>
                      <SectionActionsFooter>
                        <FooterActionRow onClick={() => openAddTimeslot(timelineSection.section.id)}>
                          <FooterActionIcon>
                            <FooterActionIconMark>+</FooterActionIconMark>
                          </FooterActionIcon>
                          <FooterActionText>
                            <FooterActionTitle>Add Timeslot</FooterActionTitle>
                            <FooterActionMeta>Create a new slot inside this section</FooterActionMeta>
                          </FooterActionText>
                        </FooterActionRow>
                      </SectionActionsFooter>
                    </SectionCard>
                    <TimelineAddSectionRow>
                      <TimelineAddSectionRail>
                        <TimelineBottomConnector />
                        <AddSectionRailButton type="button" aria-label="Add" onClick={openAddSection}>
                          <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 448 512" height="22" width="22" xmlns="http://www.w3.org/2000/svg">
                            <path d="M416 208H272V64c0-17.67-14.33-32-32-32h-32c-17.67 0-32 14.33-32 32v144H32c-17.67 0-32 14.33-32 32v32c0 17.67 14.33 32 32 32h144v144c0 17.67 14.33 32 32 32h32c17.67 0 32-14.33 32-32V304h144c17.67 0 32-14.33 32-32v-32c0-17.67-14.33-32-32-32z" />
                          </svg>
                        </AddSectionRailButton>
                        <AddSectionRailText>Add Section</AddSectionRailText>
                      </TimelineAddSectionRail>
                      <TimelineAddSectionSpacer />
                    </TimelineAddSectionRow>
                    </TimelineSectionRow>
                  </StaggerItem>
                );
              })}
              <ServiceDurationSummary>
                <ServiceDurationSummaryLabel>Service Total</ServiceDurationSummaryLabel>
                <ServiceDurationSummaryValue>{formatPlannerDuration(serviceDurationMinutes)}</ServiceDurationSummaryValue>
              </ServiceDurationSummary>
            </SectionList>
          )}
        </PlannerCard>
      ) : null}

      {activeTab === 'Teams' ? (
        <PlannerCard>
          <PlannerHeading>Teams</PlannerHeading>
          <PlannerSubtle>Service-wide role assignments.</PlannerSubtle>
          {visibleGroupedRoles.length === 0 ? (
            <PlannerSubtle>No team roles configured.</PlannerSubtle>
          ) : (
            visibleGroupedRoles.map((group, sectionIndex) => (
              <StaggerItem key={group.id} $delayMs={Math.min(sectionIndex * 55, 320)}>
                <TeamSectionBlock>
                  <TeamSectionHeader>
                    <TeamSectionTitle>{group.label}</TeamSectionTitle>
                    <CountBadge>{group.roles.length} roles</CountBadge>
                  </TeamSectionHeader>
                  <TeamGrid>
                    {group.roles.map((role) => {
                      const assignment = roleAssignmentsByRole.get(role.id);
                      const assignedUser = firstRelation(assignment?.assigned_user);
                      const assigneeName = assignedUser?.full_name || assignedUser?.email || 'Unassigned';
                      const isUnassigned = !assignment?.user_id;
                      const assigneeAvatar = assignedUser?.avatar || null;
                      const tone = getAssignmentTone(assignment?.status);
                      const assignmentLabel = formatPlannerStatusLabel(normalizePlannerStatus(assignment?.status));
                      const isSavingRole = teamAssignmentRoleId === role.id;
                      return (
                        <TeamCard key={role.id}>
                          <TeamRoleTitle>{role.name}</TeamRoleTitle>
                          <TeamPersonRow>
                            {assigneeAvatar ? (
                              <TeamPersonAvatar
                                src={assigneeAvatar}
                                alt={assigneeName}
                                loading="lazy"
                                referrerPolicy="no-referrer"
                              />
                            ) : (
                              <TeamPersonAvatarFallback>{personInitial(assigneeName)}</TeamPersonAvatarFallback>
                            )}
                            <TeamPersonName $unassigned={isUnassigned} title={assigneeName}>
                              {assigneeName}
                            </TeamPersonName>
                          </TeamPersonRow>
                          <TeamMeta>{assignedUser?.title || assignedUser?.email || 'Planner member'}</TeamMeta>
                          {assignmentLabel !== 'AVAILABLE' ? (
                            <TeamStatusPill $tone={tone}>{assignmentLabel}</TeamStatusPill>
                          ) : null}
                          {canManageTeamAssignments ? (
                            <TeamActionRow>
                              <TeamActionButton
                                onClick={() => openTeamMemberPicker(role.id)}
                                disabled={isSavingRole}
                              >
                                {isSavingRole ? 'Saving...' : assignment?.user_id ? 'Reassign' : 'Assign'}
                              </TeamActionButton>
                              {assignment?.user_id ? (
                                <BackButton
                                  onClick={() => void handleAssignTeamRole(role.id, null)}
                                  disabled={isSavingRole}
                                >
                                  Unassign
                                </BackButton>
                              ) : null}
                            </TeamActionRow>
                          ) : null}
                        </TeamCard>
                      );
                    })}
                  </TeamGrid>
                </TeamSectionBlock>
              </StaggerItem>
            ))
          )}
        </PlannerCard>
      ) : null}

      {activeTab === 'Files' ? (
        <PlannerCard>
          <PlannerHeading>Files</PlannerHeading>
          <PlannerSubtle>Attachments for this service plan.</PlannerSubtle>
          {(plan.service_plan_media_items || []).length === 0 ? (
            <PlannerSubtle>No files attached yet.</PlannerSubtle>
          ) : (
            <FileList>
              {groupedFiles.map((group, sectionIndex) => (
                <StaggerItem key={group.category} $delayMs={Math.min(sectionIndex * 55, 320)}>
                  <FileCategoryBlock>
                    <FileCategoryHeader>
                      <FileCategoryTitle>{group.category.replace(/_/g, ' ')}</FileCategoryTitle>
                      <CountBadge>{group.items.length} files</CountBadge>
                    </FileCategoryHeader>
                    <FileGrid>
                      {group.items.map((file) => (
                        <FileRow key={file.id} href={file.file_url} target="_blank" rel="noreferrer">
                          <FileTitle>{file.title}</FileTitle>
                          <FileMetaRow>
                            <FileMetaPill>{file.media_type || 'file'}</FileMetaPill>
                            <FileMetaPill>{file.category}</FileMetaPill>
                          </FileMetaRow>
                        </FileRow>
                      ))}
                    </FileGrid>
                  </FileCategoryBlock>
                </StaggerItem>
              ))}
            </FileList>
          )}
        </PlannerCard>
      ) : null}

      {activeTab === 'Songs' ? (
        <PlannerCard>
          <PlannerHeading>Songs</PlannerHeading>
          <PlannerSubtle>
            Songs already in this service are listed below. Planner admins can search the library and add more.
          </PlannerSubtle>

          {canManageSongAssignments ? (
            <>
              <FormGroup>
                <FormLabel>Section to add songs into</FormLabel>
                <SelectInput value={songsSectionId} onChange={(event) => setSongsSectionId(event.target.value)}>
                  {sections.map((section) => (
                    <option key={section.id} value={section.id}>
                      {section.name}
                    </option>
                  ))}
                </SelectInput>
              </FormGroup>

              <FormGroup>
                <FormLabel>Search song library</FormLabel>
                <FormInput
                  value={serviceSongSearch}
                  onChange={(event) => setServiceSongSearch(event.target.value)}
                  placeholder="Type title, artist, key, or notes"
                />
              </FormGroup>

              <SongTemplateList>
                {songsLoading ? <PlannerSubtle>Loading songs...</PlannerSubtle> : null}
                {!songsLoading && !serviceSongSearch.trim() ? (
                  <PlannerSubtle>Search to find songs and add them to this service.</PlannerSubtle>
                ) : null}
                {!songsLoading && !!serviceSongSearch.trim() && !searchedSongTemplates.length ? (
                  <PlannerSubtle>No songs match this search.</PlannerSubtle>
                ) : null}
                {searchedSongTemplates.map((song) => (
                  <SongTemplateCard
                    key={`search-${song.id}`}
                    onClick={() => navigate(`/church-planner/songs/${song.id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <SongTemplateCover>{songCoverInitials(song.title, song.artist)}</SongTemplateCover>
                    <SongTemplateBody>
                      <div>
                        <SongTemplateTitle>{song.title}</SongTemplateTitle>
                        <SongTemplateMeta>{song.artist || 'Unknown artist'}</SongTemplateMeta>
                      </div>

                      <SongTemplateChipRow>
                        <SongTemplateChip>{formatSongTemplateLengthDisplay(song) || 'No duration'}</SongTemplateChip>
                        <SongTemplateChip>{song.key_signature || 'No key'}</SongTemplateChip>
                        <SongTemplateChip>{song.bpm || '--'} BPM</SongTemplateChip>
                        <SongTemplateChip>Last: {formatSongLastScheduledDate(song.last_scheduled_date)}</SongTemplateChip>
                      </SongTemplateChipRow>

                      <ButtonRow>
                        <ActionButton
                          onClick={(event) => {
                            event.stopPropagation();
                            void handleApplySongTemplate(song);
                          }}
                          disabled={applyingSongId === song.id || !songsSectionId}
                        >
                          {applyingSongId === song.id ? 'Adding...' : 'Add Slot'}
                        </ActionButton>
                      </ButtonRow>
                    </SongTemplateBody>
                  </SongTemplateCard>
                ))}
              </SongTemplateList>
            </>
          ) : null}

          <SongTemplateList>
            {songsLoading ? <PlannerSubtle>Loading songs...</PlannerSubtle> : null}
            {!songsLoading && !serviceSongTemplates.length ? (
              <PlannerSubtle>No songs have been added to this service yet.</PlannerSubtle>
            ) : null}
            {serviceSongTemplates.map((song) => (
              <SongTemplateCard
                key={song.id}
                onClick={() => {
                  if (editingSongTemplateId === song.id) return;
                  navigate(`/church-planner/songs/${song.id}`);
                }}
                style={{ cursor: editingSongTemplateId === song.id ? 'default' : 'pointer' }}
              >
                <SongTemplateCover>{songCoverInitials(song.title, song.artist)}</SongTemplateCover>
                <SongTemplateBody>
                  <div>
                    <SongTemplateTitle>{song.title}</SongTemplateTitle>
                    <SongTemplateMeta>{song.artist || 'Unknown artist'}</SongTemplateMeta>
                  </div>

                  <SongTemplateChipRow>
                    <SongTemplateChip>{formatSongTemplateLengthDisplay(song) || 'No duration'}</SongTemplateChip>
                    <SongTemplateChip>{song.key_signature || 'No key'}</SongTemplateChip>
                    <SongTemplateChip>{song.bpm || '--'} BPM</SongTemplateChip>
                    <SongTemplateChip>Last: {formatSongLastScheduledDate(song.last_scheduled_date)}</SongTemplateChip>
                    
                    {song.video_url ? <SongTemplateChip>Video</SongTemplateChip> : null}
                  </SongTemplateChipRow>

                  {editingSongTemplateId === song.id ? (
                    <>
                      <SongsControlGrid>
                        <FormGroup>
                          <FormLabel>Song title</FormLabel>
                          <FormInput value={editSongTitle} onChange={(event) => setEditSongTitle(event.target.value)} />
                        </FormGroup>
                        <FormGroup>
                          <FormLabel>Artist</FormLabel>
                          <FormInput value={editSongArtist} onChange={(event) => setEditSongArtist(event.target.value)} />
                        </FormGroup>
                        <FormGroup>
                          <FormLabel>Key</FormLabel>
                          <FormInput value={editSongKey} onChange={(event) => setEditSongKey(event.target.value)} />
                        </FormGroup>
                        <FormGroup>
                          <FormLabel>Length minutes</FormLabel>
                          <FormInput value={editSongLength} onChange={(event) => setEditSongLength(event.target.value)} />
                        </FormGroup>
                        <FormGroup>
                          <FormLabel>BPM</FormLabel>
                          <FormInput value={editSongBpm} onChange={(event) => setEditSongBpm(event.target.value)} />
                        </FormGroup>
                        <FormGroup>
                          <FormLabel>Sequence</FormLabel>
                          <FormInput value={editSongSequence} onChange={(event) => setEditSongSequence(event.target.value)} />
                        </FormGroup>
                        <FormGroup>
                          <FormLabel>Lyrics URL</FormLabel>
                          <FormInput value={editSongLyricsUrl} onChange={(event) => setEditSongLyricsUrl(event.target.value)} />
                        </FormGroup>
                        <FormGroup>
                          <FormLabel>Chord chart URL</FormLabel>
                          <FormInput value={editSongChordUrl} onChange={(event) => setEditSongChordUrl(event.target.value)} />
                        </FormGroup>
                        <FormGroup style={{ gridColumn: '1 / -1' }}>
                          <FormLabel>Notes</FormLabel>
                          <FormTextarea value={editSongNotes} onChange={(event) => setEditSongNotes(event.target.value)} />
                        </FormGroup>
                      </SongsControlGrid>
                      <ButtonRow>
                        <BackButton onClick={cancelInlineSongTemplateEdit} disabled={songsSaving}>Cancel</BackButton>
                        <ActionButton onClick={() => void handleUpdateSongTemplate()} disabled={songsSaving}>
                          {songsSaving ? 'Saving...' : 'Save Changes'}
                        </ActionButton>
                      </ButtonRow>
                    </>
                  ) : (
                    canManageSongAssignments ? (
                      <ButtonRow>
                        <BackButton
                          onClick={(event) => {
                            event.stopPropagation();
                            startInlineSongTemplateEdit(song);
                          }}
                        >
                          Edit
                        </BackButton>
                        <BackButton
                          onClick={(event) => {
                            event.stopPropagation();
                            void handleDeleteSongTemplate(song.id);
                          }}
                        >
                          Delete
                        </BackButton>
                      </ButtonRow>
                    ) : null
                  )}
                </SongTemplateBody>
              </SongTemplateCard>
            ))}
          </SongTemplateList>
        </PlannerCard>
      ) : null}

      {editServiceOpen ? (
        <Overlay>
          <PickerCard>
            <ModalTitle>Edit Service</ModalTitle>
            <FormGroup>
              <FormLabel>Title</FormLabel>
              <FormInput value={editServiceTitle} onChange={(event) => setEditServiceTitle(event.target.value)} />
            </FormGroup>
            <FormGroup>
              <FormLabel>Date</FormLabel>
              <FormInput type="date" value={editServiceDate} onChange={(event) => setEditServiceDate(event.target.value)} />
            </FormGroup>
            <FormGroup>
              <FormLabel>Service Time</FormLabel>
              <FormInput type="time" value={editServiceTime} onChange={(event) => setEditServiceTime(event.target.value)} />
            </FormGroup>
            <FormGroup>
              <FormLabel>Start Time</FormLabel>
              <FormInput type="time" value={editServiceStartTime} onChange={(event) => setEditServiceStartTime(event.target.value)} />
            </FormGroup>
            <ToggleRow>
              <ToggleText>
                <ToggleTitle>Auto-assign team roles</ToggleTitle>
                <ToggleHelp>When off, empty roles stay open until someone assigns them manually.</ToggleHelp>
              </ToggleText>
              <ToggleCheckbox
                type="checkbox"
                checked={editServiceAutoAssignEnabled}
                onChange={(event) => setEditServiceAutoAssignEnabled(event.target.checked)}
              />
            </ToggleRow>
            <FormGroup>
              <FormLabel>Location</FormLabel>
              <FormInput value={editServiceLocation} onChange={(event) => setEditServiceLocation(event.target.value)} />
            </FormGroup>
            <FormGroup>
              <FormLabel>Notes</FormLabel>
              <FormTextarea value={editServiceNotes} onChange={(event) => setEditServiceNotes(event.target.value)} />
            </FormGroup>
            <FormGroup>
              <FormLabel>Load Service Template</FormLabel>
              <SelectInput
                value={selectedEditServiceTemplateId}
                onChange={(event) => setSelectedEditServiceTemplateId(event.target.value)}
                disabled={loadingEditServiceTemplate || planner.templatesQuery.isLoading}
              >
                <option value="">No template selected</option>
                {planner.serviceTemplates.map((template) => (
                  <option key={template.id} value={template.id}>{template.name}</option>
                ))}
              </SelectInput>
            </FormGroup>
            <ButtonRow>
              <BackButton onClick={() => setEditServiceOpen(false)}>Cancel</BackButton>
              <ActionButton
                onClick={() => void handleLoadServiceTemplate()}
                disabled={loadingEditServiceTemplate || !selectedEditServiceTemplateId}
              >
                {loadingEditServiceTemplate ? 'Loading...' : 'Load Template'}
              </ActionButton>
              <ActionButton onClick={() => void handleUpdateService()}>Save</ActionButton>
            </ButtonRow>
          </PickerCard>
        </Overlay>
      ) : null}

      {teamPositionOpen ? (
        <Overlay>
          <PickerCard>
            <ModalTitle>Add Team Position</ModalTitle>
            <FormGroup>
              <FormLabel>Team Area</FormLabel>
              <FormInput value={newRoleArea} onChange={(event) => setNewRoleArea(event.target.value)} placeholder="production, band, praise" />
            </FormGroup>
            <FormGroup>
              <FormLabel>Position Name</FormLabel>
              <FormInput value={newRoleName} onChange={(event) => setNewRoleName(event.target.value)} placeholder="FOH Audio" />
            </FormGroup>
            <ButtonRow>
              <BackButton onClick={() => setTeamPositionOpen(false)}>Cancel</BackButton>
              <ActionButton onClick={() => void handleAddTeamPosition()}>Add</ActionButton>
            </ButtonRow>
          </PickerCard>
        </Overlay>
      ) : null}

      {addSectionOpen ? (
        <Overlay>
          <PickerCard>
            <ModalTitle>Add Section</ModalTitle>
            <FormGroup>
              <FormLabel>Section Title</FormLabel>
              <SelectInput
                value={isCustomSectionTitle ? '__custom__' : selectedSectionTitle}
                onChange={(event) => {
                  const value = event.target.value;
                  if (value === '__custom__') {
                    setIsCustomSectionTitle(true);
                    setSelectedSectionTitle('');
                    return;
                  }

                  setIsCustomSectionTitle(false);
                  setSelectedSectionTitle(value);
                }}
              >
                <option value="">Select predefined section title</option>
                {DEFAULT_ORDER_SECTIONS.map((title) => (
                  <option key={title} value={title}>{title}</option>
                ))}
                <option value="__custom__">Add new title...</option>
              </SelectInput>
            </FormGroup>
            {isCustomSectionTitle ? (
              <FormGroup>
                <FormLabel>New Section Title</FormLabel>
                <FormInput
                  value={newSectionName}
                  onChange={(event) => setNewSectionName(event.target.value)}
                  placeholder="Enter custom section title"
                />
              </FormGroup>
            ) : null}
            <ButtonRow>
              <BackButton
                onClick={() => {
                  setAddSectionOpen(false);
                  setSelectedSectionTitle('');
                  setIsCustomSectionTitle(false);
                  setNewSectionName('');
                }}
              >
                Cancel
              </BackButton>
              <ActionButton onClick={() => void handleAddSection()}>Add</ActionButton>
            </ButtonRow>
          </PickerCard>
        </Overlay>
      ) : null}

      {editSectionId ? (
        <Overlay>
          <PickerCard>
            <ModalTitle>Edit Section</ModalTitle>
            <FormGroup>
              <FormLabel>Section Name</FormLabel>
              <FormInput
                value={editSectionName}
                onChange={(event) => setEditSectionName(event.target.value)}
                placeholder="Enter section title"
              />
            </FormGroup>
            <ButtonRow>
              <BackButton onClick={closeEditSection}>Cancel</BackButton>
              <ActionButton onClick={() => void handleUpdateSection()}>Save</ActionButton>
            </ButtonRow>
          </PickerCard>
        </Overlay>
      ) : null}

      {addTimeslotOpen ? (
        <Overlay>
          <PickerCardTall>
            <HeaderRow>
              <ModalTitleInline>
                <ModalTitle>Add Timeslot</ModalTitle>
                {selectedNewTimeslotTaggedMembers.length ? (
                  <CountBadge>{selectedNewTimeslotTaggedMembers.length} tagged</CountBadge>
                ) : null}
              </ModalTitleInline>
            </HeaderRow>
            <ModalBody>
              <FormGroup>
                <FormLabel>Section</FormLabel>
                <SelectInput value={newTimeslotSectionId} onChange={(event) => setNewTimeslotSectionId(event.target.value)}>
                  {sections.map((section) => (
                    <option key={section.id} value={section.id}>
                      {section.name}
                    </option>
                  ))}
                </SelectInput>
              </FormGroup>
              <FormGroup>
                <FormLabel>Timeslot Name</FormLabel>
                <FormInput value={newTimeslotName} onChange={(event) => setNewTimeslotName(event.target.value)} placeholder="Welcome + Opening Prayer" />
              </FormGroup>
              <FormGroup>
                <FormLabel>Description</FormLabel>
                <FormTextarea value={newTimeslotDescription} onChange={(event) => setNewTimeslotDescription(event.target.value)} />
              </FormGroup>
              <FormGroup>
                <FormLabel>Song Template (optional)</FormLabel>
                <FormInput
                  value={newTimeslotSongSearch}
                  onChange={(event) => handleNewTimeslotSongSearchChange(event.target.value)}
                  placeholder="Search by title or artist"
                />
                <SelectInput
                  value={newTimeslotSongTemplateId}
                  onChange={(event) => {
                    handleSelectNewTimeslotSongTemplate(event.target.value);
                  }}
                  disabled={creatingTimeslotSongTemplate}
                >
                  <option value="">No song template</option>
                  {timeslotSongSelectOptions.map((song) => (
                    <option key={song.id} value={song.id}>
                      {song.title}
                      {song.artist ? ` - ${song.artist}` : ''}
                      {song.length_minutes > 0 ? ` (${song.length_minutes}m)` : ''}
                    </option>
                  ))}
                </SelectInput>
                {!timeslotSongSelectOptions.length ? (
                  <>
                    <EmptyHint>No songs match this search.</EmptyHint>
                    {newTimeslotSongSearch.trim() ? (
                      <ButtonRow style={{ justifyContent: 'flex-start' }}>
                        <ActionButton
                          onClick={() => void handleQuickCreateTimeslotSongTemplate()}
                          disabled={creatingTimeslotSongTemplate}
                        >
                          {creatingTimeslotSongTemplate
                            ? 'Creating...'
                            : `Create "${newTimeslotSongSearch.trim()}"`}
                        </ActionButton>
                      </ButtonRow>
                    ) : null}
                  </>
                ) : null}
              </FormGroup>
              <PlannerKicker>Tag Person</PlannerKicker>
              <SearchInput
                value={pickerSearch}
                onChange={(event) => setPickerSearch(event.target.value)}
                placeholder="Search member by name or email"
              />
              {!pickerSearch.trim() ? <PlannerSubtle>Type a name or email to find members.</PlannerSubtle> : null}
              {pickerSearch.trim() && pickerLoading ? <PlannerSubtle>Loading members...</PlannerSubtle> : null}
              {selectedNewTimeslotTaggedMembers.length ? (
                <ChipRow>
                  {selectedNewTimeslotTaggedMembers.map((member) => {
                    const label = member.full_name || member.email || member.id;
                    return (
                      <PersonChip key={member.id}>
                        {member.avatar ? (
                          <PersonAvatar
                            src={member.avatar}
                            alt={label}
                            loading="lazy"
                            referrerPolicy="no-referrer"
                          />
                        ) : (
                          <PersonAvatarFallback>{personInitial(label)}</PersonAvatarFallback>
                        )}
                        <PersonChipName title={label}>{label}</PersonChipName>
                        <RemoveChipButton
                          aria-label="Remove tagged person"
                          onClick={() => {
                            setNewTimeslotTaggedMemberIds((current) => current.filter((id) => id !== member.id));
                          }}
                        >
                          x
                        </RemoveChipButton>
                      </PersonChip>
                    );
                  })}
                </ChipRow>
              ) : null}
              {pickerSearch.trim() ? (
                <PickerList>
                  {pickerMembers.length === 0 && !pickerLoading ? (
                    <EmptyHint>No members found.</EmptyHint>
                  ) : (
                    pickerMembers.map((member) => {
                      const blockReason = getAvailabilityBlockReasonForUserId(member.id);
                      const isBlocked = Boolean(blockReason);
                      return (
                        <PickerItem
                          key={member.id}
                          onClick={() => {
                            setTaggedMemberCache((current) => ({ ...current, [member.id]: member }));
                            setNewTimeslotTaggedMemberIds((current) =>
                              current.includes(member.id) ? current : [...current, member.id]
                            );
                          }}
                          disabled={newTimeslotTaggedMemberIds.includes(member.id) || isBlocked}
                        >
                          <strong>{member.full_name || member.email || member.id}</strong>
                          <div>{blockReason ? `Blocked: ${blockReason}` : (member.title || member.email || 'Planner member')}</div>
                        </PickerItem>
                      );
                    })
                  )}
                </PickerList>
              ) : null}
              <FormGroup>
                <FormLabel>Length</FormLabel>
                <FormInput
                  value={newTimeslotLength}
                  onChange={(event) => setNewTimeslotLength(event.target.value)}
                  placeholder="10 or 9:30"
                />
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <BackButton
                onClick={() => {
                  setAddTimeslotOpen(false);
                  setNewTimeslotSongTemplateId('');
                  setNewTimeslotSongSearch('');
                  setNewTimeslotTaggedMemberIds([]);
                  setTaggedMemberCache({});
                  setPickerSearch('');
                  setPickerMembers([]);
                }}
              >
                Cancel
              </BackButton>
              <ActionButton onClick={() => void handleAddTimeslot()}>Add Timeslot</ActionButton>
            </ModalFooter>
          </PickerCardTall>
        </Overlay>
      ) : null}

      {editTimeslotId ? (
        <Overlay>
          <PickerCardTall>
            <HeaderRow>
              <ModalTitleInline>
                <ModalTitle>Edit Timeslot</ModalTitle>
                {editTimeslotTaggedMemberIds.length ? (
                  <CountBadge>{editTimeslotTaggedMemberIds.length} tagged</CountBadge>
                ) : null}
              </ModalTitleInline>
              <BackButton onClick={closeTimeslotEditor}>Close</BackButton>
            </HeaderRow>
            <ModalBody>
              <FormGroup>
                <FormLabel>Timeslot Name</FormLabel>
                <FormInput value={editTimeslotName} onChange={(event) => setEditTimeslotName(event.target.value)} />
              </FormGroup>
              <FormGroup>
                <FormLabel>Description</FormLabel>
                <FormTextarea value={editTimeslotDescription} onChange={(event) => setEditTimeslotDescription(event.target.value)} />
              </FormGroup>
              <FormGroup>
                <FormLabel>Song Template (optional)</FormLabel>
                <FormInput
                  value={editTimeslotSongSearch}
                  onChange={(event) => handleEditTimeslotSongSearchChange(event.target.value)}
                  placeholder="Search by title or artist"
                />
                <SelectInput
                  value={editTimeslotSongTemplateId}
                  onChange={(event) => {
                    handleSelectEditTimeslotSongTemplate(event.target.value);
                  }}
                  disabled={creatingTimeslotSongTemplate}
                >
                  <option value="">No song template</option>
                  {editTimeslotSongSelectOptions.map((song) => (
                    <option key={song.id} value={song.id}>
                      {song.title}
                      {song.artist ? ` - ${song.artist}` : ''}
                      {song.length_minutes > 0 ? ` (${song.length_minutes}m)` : ''}
                    </option>
                  ))}
                </SelectInput>
                {!editTimeslotSongSelectOptions.length ? (
                  <>
                    <EmptyHint>No songs match this search.</EmptyHint>
                    {editTimeslotSongSearch.trim() ? (
                      <ButtonRow style={{ justifyContent: 'flex-start' }}>
                        <ActionButton
                          onClick={() => void handleQuickCreateEditTimeslotSongTemplate()}
                          disabled={creatingTimeslotSongTemplate}
                        >
                          {creatingTimeslotSongTemplate
                            ? 'Creating...'
                            : `Create "${editTimeslotSongSearch.trim()}"`}
                        </ActionButton>
                      </ButtonRow>
                    ) : null}
                  </>
                ) : null}
              </FormGroup>
              <FormGroup>
                <FormLabel>Length (minutes)</FormLabel>
                <FormInput value={editTimeslotLength} onChange={(event) => setEditTimeslotLength(event.target.value)} placeholder="10" />
              </FormGroup>
              <PlannerKicker>Tag Person</PlannerKicker>
              <SearchInput
                value={pickerSearch}
                onChange={(event) => setPickerSearch(event.target.value)}
                placeholder="Search member by name or email"
              />
              {!pickerSearch.trim() ? <PlannerSubtle>Type a name or email to find members.</PlannerSubtle> : null}
              {pickerSearch.trim() && pickerLoading ? <PlannerSubtle>Loading members...</PlannerSubtle> : null}
              {selectedEditTimeslotTaggedMembers.length ? (
                <ChipRow>
                  {selectedEditTimeslotTaggedMembers.map((member) => {
                    const label = member.full_name || member.email || member.id;
                    return (
                      <PersonChip key={member.id}>
                        {member.avatar ? (
                          <PersonAvatar
                            src={member.avatar}
                            alt={label}
                            loading="lazy"
                            referrerPolicy="no-referrer"
                          />
                        ) : (
                          <PersonAvatarFallback>{personInitial(label)}</PersonAvatarFallback>
                        )}
                        <PersonChipName title={label}>{label}</PersonChipName>
                        <RemoveChipButton
                          aria-label="Remove tagged person"
                          onClick={() => {
                            setEditTimeslotTaggedMemberIds((current) => current.filter((id) => id !== member.id));
                          }}
                        >
                          x
                        </RemoveChipButton>
                      </PersonChip>
                    );
                  })}
                </ChipRow>
              ) : null}
              {pickerSearch.trim() ? (
                <PickerList>
                  {pickerMembers.length === 0 && !pickerLoading ? (
                    <EmptyHint>No members found.</EmptyHint>
                  ) : (
                    pickerMembers.map((member) => {
                      const blockReason = getAvailabilityBlockReasonForUserId(member.id);
                      const isBlocked = Boolean(blockReason);
                      return (
                        <PickerItem
                          key={member.id}
                          onClick={() => {
                            setTaggedMemberCache((current) => ({ ...current, [member.id]: member }));
                            setEditTimeslotTaggedMemberIds((current) =>
                              current.includes(member.id) ? current : [...current, member.id]
                            );
                          }}
                          disabled={editTimeslotTaggedMemberIds.includes(member.id) || isBlocked}
                        >
                          <strong>{member.full_name || member.email || member.id}</strong>
                          <div>{blockReason ? `Blocked: ${blockReason}` : (member.title || member.email || 'Planner member')}</div>
                        </PickerItem>
                      );
                    })
                  )}
                </PickerList>
              ) : null}
            </ModalBody>
            <ModalFooter>
              <ActionButton onClick={() => void handleUpdateTimeslot()}>Save Timeslot</ActionButton>
            </ModalFooter>
          </PickerCardTall>
        </Overlay>
      ) : null}

      {teamPickerRoleId ? (
        <Overlay>
          <PickerCardTall>
            <HeaderRow>
              <ModalTitleInline>
                <ModalTitle>
                  Assign Member{activeTeamPickerRole?.name ? ` - ${activeTeamPickerRole.name}` : ''}
                </ModalTitle>
              </ModalTitleInline>
              <BackButton onClick={() => setTeamPickerRoleId(null)}>Close</BackButton>
            </HeaderRow>
            <ModalBody>
              <SearchInput
                value={teamMemberSearch}
                onChange={(event) => setTeamMemberSearch(event.target.value)}
                placeholder="Search member by name or email"
              />
              {pickerLoading ? <PlannerSubtle>Loading members...</PlannerSubtle> : null}
              <PickerList>
                <PickerItem
                  onClick={() => void handleAssignTeamRole(teamPickerRoleId, null)}
                  disabled={teamAssignmentRoleId === teamPickerRoleId}
                >
                  <strong>Unassign role</strong>
                  <div>Clear the member assigned to this position.</div>
                </PickerItem>
                {teamPickerMembers.length === 0 && !pickerLoading ? (
                  <EmptyHint>No members found.</EmptyHint>
                ) : (
                  teamPickerMembers.map((member) => {
                    const label = member.full_name || member.email || member.id;
                    const blockReason = getTeamMemberBlockReason(member);
                    const isBlocked = Boolean(blockReason);
                    return (
                      <PickerItem
                        key={member.id}
                        onClick={() => void handleAssignTeamRole(teamPickerRoleId, member.id)}
                        disabled={teamAssignmentRoleId === teamPickerRoleId || isBlocked}
                      >
                        <strong>{label}</strong>
                        <div>{blockReason ? `Blocked: ${blockReason}` : (member.title || member.email || 'Planner member')}</div>
                      </PickerItem>
                    );
                  })
                )}
              </PickerList>
            </ModalBody>
            <ModalFooter>
              <BackButton onClick={() => setTeamPickerRoleId(null)}>Cancel</BackButton>
            </ModalFooter>
          </PickerCardTall>
        </Overlay>
      ) : null}
    </PlannerPage>
  );
}
